import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, Upload, X, Pen, Save } from 'lucide-react';
import { message, Spin } from 'antd';

import axios from 'axios';
import AppLayout from '../components/Layout/AppLayout';
import { useAuthToken } from '../hooks/useAuthToken';
import { useVendor } from '../context/VendorContext';
import { api, getS3Url } from '../services/api';
import { getAvatarUrl, s3KeyToSeed } from '../utils/avatars';

const PAGE_BG = 'bg-[#f0f2f5]';
const CARD =
  'rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)]';
/** Distinct cool tint so the live preview reads separately from white input cards */
const CARD_PAD = 'p-6 sm:p-8';

const BUYER_MOOD_PROMPTS = {
  SKEPTICAL:
    'Challenge every claim before accepting it. Ask for evidence and show mild suspicion toward anything that sounds too good to be true.',
  FRUSTRATED:
    'Push back quickly on anything that sounds like a pitch. Reference past disappointments when the topic is relevant and keep a sharper, impatient tone.',
  DEFENSIVE:
    'Treat suggestions to change the current setup as implicit criticism. Require the rep to prove clear superiority before you engage.',
  RUSHED:
    'Signal early that time is short. Cut off long explanations and show impatience when the rep repeats or stays generic.',
};

const MOOD_PRESSURE_TO_BEHAVIOR = {
  SKEPTICAL: {
    BUDGET_PRESSURE:    { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    COMPETITIVE_EVALUATION: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    COMPETITIVE_THREAT: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    INTERNAL_DEADLINE:  { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    DEADLINE_CRUNCH:    { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    PROCUREMENT_PROCESS: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    TECHNICAL_VALIDATION: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    LEADERSHIP_APPROVAL: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    EXISTING_VENDOR_CONTRACT: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    RESOURCE_CONSTRAINTS: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    CHURN_RISK:         { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    LEADERSHIP_CHANGE:  { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    SECURITY_REVIEW:    { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    LEGAL_REVIEW:       { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    ORGANIZATIONAL_RESTRUCTURING: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    BOARD_PRESSURE:     { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    EXECUTIVE_MANDATE:  { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    ROI_SCRUTINY:       { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    CHANGE_RESISTANCE:  { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
  },
  FRUSTRATED: {
    BUDGET_PRESSURE:    { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    COMPETITIVE_EVALUATION: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    COMPETITIVE_THREAT: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    INTERNAL_DEADLINE:  { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    DEADLINE_CRUNCH:    { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    PROCUREMENT_PROCESS: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    TECHNICAL_VALIDATION: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    LEADERSHIP_APPROVAL: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    EXISTING_VENDOR_CONTRACT: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    RESOURCE_CONSTRAINTS: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    CHURN_RISK:         { tone: 'WARM',    reply: 'COOPERATIVE' },
    LEADERSHIP_CHANGE:  { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    SECURITY_REVIEW:    { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    LEGAL_REVIEW:       { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    ORGANIZATIONAL_RESTRUCTURING: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    BOARD_PRESSURE:     { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    EXECUTIVE_MANDATE:  { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    ROI_SCRUTINY:       { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
    CHANGE_RESISTANCE:  { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
  },
  DEFENSIVE: {
    BUDGET_PRESSURE:    { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    COMPETITIVE_EVALUATION: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    COMPETITIVE_THREAT: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    INTERNAL_DEADLINE:  { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    DEADLINE_CRUNCH:    { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    PROCUREMENT_PROCESS: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    TECHNICAL_VALIDATION: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    LEADERSHIP_APPROVAL: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    EXISTING_VENDOR_CONTRACT: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    RESOURCE_CONSTRAINTS: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    CHURN_RISK:         { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    LEADERSHIP_CHANGE:  { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    SECURITY_REVIEW:    { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    LEGAL_REVIEW:       { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    ORGANIZATIONAL_RESTRUCTURING: { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    BOARD_PRESSURE:     { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    EXECUTIVE_MANDATE:  { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    ROI_SCRUTINY:       { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
    CHANGE_RESISTANCE:  { tone: 'NEUTRAL', reply: 'ACTIVELY_RESISTS' },
  },
  RUSHED: {
    BUDGET_PRESSURE:    { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    COMPETITIVE_EVALUATION: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    COMPETITIVE_THREAT: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    INTERNAL_DEADLINE:  { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    DEADLINE_CRUNCH:    { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    PROCUREMENT_PROCESS: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    TECHNICAL_VALIDATION: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    LEADERSHIP_APPROVAL: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    EXISTING_VENDOR_CONTRACT: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    RESOURCE_CONSTRAINTS: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    CHURN_RISK:         { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    LEADERSHIP_CHANGE:  { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    SECURITY_REVIEW:    { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    LEGAL_REVIEW:       { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    ORGANIZATIONAL_RESTRUCTURING: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    BOARD_PRESSURE:     { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    EXECUTIVE_MANDATE:  { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    ROI_SCRUTINY:       { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
    CHANGE_RESISTANCE:  { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
  },
};

const MOOD_DEFAULT_BEHAVIOR = {
  CURIOUS: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
  FRIENDLY: { tone: 'WARM', reply: 'COOPERATIVE' },
  OPEN_MINDED: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
  ANALYTICAL: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
  RESERVED_BUT_ENGAGED: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
  BUSY: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
  CAUTIOUS: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
  DISTRACTED: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
  AGGRESSIVE: { tone: 'RUDE', reply: 'ACTIVELY_RESISTS' },
  DEMANDING: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
  IMPATIENT: { tone: 'NEUTRAL', reply: 'COOPERATIVE' },
  UNTRUSTING: { tone: 'RUDE', reply: 'ACTIVELY_RESISTS' },
  POLITICALLY_CAREFUL: { tone: 'NEUTRAL', reply: 'NEEDS_CONVINCING' },
};

const BUYER_PRESSURE_PROMPTS = {
  BUDGET_PRESSURE:
    'Leadership is cutting software spend before quarter-end. Challenge every cost, ask what this replaces, and demand total cost of ownership upfront.',
  COMPETITIVE_EVALUATION:
    'The buyer is comparing multiple vendors side by side. Ask probing questions about their evaluation criteria and push to differentiate from alternatives.',
  COMPETITIVE_THREAT:
    'A competitor is gaining ground. Ask for measurable speed or quality advantage and reject vague differentiation claims.',
  INTERNAL_DEADLINE:
    'An internal deadline is approaching fast. Keep returning to speed-to-value and short-term proof.',
  DEADLINE_CRUNCH:
    'A board review is near and progress must be shown fast. Keep returning to speed-to-value and short-term proof.',
  PROCUREMENT_PROCESS:
    'Procurement has strict approval gates. Add documentation friction and require clear compliance before moving forward.',
  TECHNICAL_VALIDATION:
    'The technical team needs to validate fit before any decision. Ask architecture-level questions and demand proof of integration compatibility.',
  LEADERSHIP_APPROVAL:
    'Executive sign-off is required. Raise the bar on business justification and ask who else needs to be convinced.',
  EXISTING_VENDOR_CONTRACT:
    'A current vendor contract is in place. Challenge the switching cost and ask what concrete improvement justifies the disruption.',
  RESOURCE_CONSTRAINTS:
    'Team bandwidth is stretched thin. Question the implementation effort and demand proof that this won\'t drain headcount.',
  LEADERSHIP_CHANGE:
    'A new executive is reviewing commitments. Add approval friction, ask who else must sign off, and avoid premature commitment.',
  CHURN_RISK:
    'Retention is dropping and NPS is under pressure. Frame questions through churn prevention and ask for evidence this would reduce losses.',
  SECURITY_REVIEW:
    'Security and compliance are top of mind. Require detailed data handling, encryption, and certification proof before proceeding.',
  LEGAL_REVIEW:
    'Legal needs to vet terms before any agreement. Push back on standard clauses and ask for flexibility on liability and data ownership.',
  ORGANIZATIONAL_RESTRUCTURING:
    'The org is going through changes. Question long-term commitments and ask how the solution adapts to shifting priorities.',
  BOARD_PRESSURE:
    'The board is watching this decision closely. Demand board-ready justification and cast doubt on anything that lacks executive-level proof.',
  EXECUTIVE_MANDATE:
    'Senior leadership has issued a directive on this initiative. Reference the mandate, expect alignment with executive priorities, and resist solutions that do not clearly support it.',
  ROI_SCRUTINY:
    'Every dollar is being scrutinized. Require hard ROI numbers and dismiss vague efficiency claims without specific projections.',
  CHANGE_RESISTANCE:
    'The organization is resistant to yet another new tool. Question the change management plan and demand proof of adoption success.',
};

const COMMUNICATION_TONE_PROMPTS = {
  NEUTRAL:
    'Respond professionally and stay measured. Acknowledge what the rep said, remain on topic, and avoid unnecessary small talk.',
  WARM:
    'Be warm, conversational, and collaborative. Volunteer useful context and show interest when the rep is relevant to your goals.',
  RUDE:
    'Be blunt and impatient. Challenge weak claims, pressure vague responses, and only soften when the rep stays evidence-based.',
};

const REPLY_BEHAVIOUR_PROMPTS = {
  COOPERATIVE:
    'Engage constructively and answer discovery questions with useful detail. Move the conversation forward when value is clear.',
  NEEDS_CONVINCING:
    'Start guarded and push back by default. Share partial context, but require specific proof before giving deeper commitment signals.',
  ACTIVELY_RESISTS:
    'Actively resist generic selling. Keep answers short, challenge most statements, and raise strong objections early if value is unclear.',
};

const CALL_FLOW_PROMPT_DEFAULT = `PROGRESSIVE CONVERSATION (CALL ARC):
The call must move forward naturally. Avoid looping. Each stage builds on the previous one.
Stage 1 — Opening (exchanges 1-2): State your initial position based on your persona.
Stage 2 — Discovery (exchanges 3-5): Let the seller ask questions. Provide answers aligned with your scenario brief.
Stage 3 — Scrutiny (exchanges 6-8): Challenge the seller's assumptions. Raise concerns about fit, price, or timing.
Stage 4 — Objection Handling (exchanges 9-11): Resist quick resolutions. Hold firm on your concerns unless the seller genuinely addresses them.
Stage 5 — Decision (exchanges 12-14): Either move toward a concrete next step (if value is demonstrated) or end the call (if not).
Typical call length: 10-14 exchanges. Drive toward a natural outcome.`;


const ENUM_OPTIONS = {
  mode: [
    { value: 'COACH', label: 'Coach Mode' },
    { value: 'SALES', label: 'Sales Mode' },
  ],
  language: [
    { value: 'ENGLISH', label: 'English' },
    { value: 'GERMAN', label: 'German' },
    { value: 'DUTCH', label: 'Dutch' },
    { value: 'ITALIAN', label: 'Italian' },
    { value: 'JAPANESE', label: 'Japanese' },
    { value: 'SPANISH', label: 'Spanish' },
    { value: 'FRENCH', label: 'French' },
    { value: 'TAMIL', label: 'Tamil' },
    { value: 'KANNADA', label: 'Kannada' },
    { value: 'HINDI', label: 'Hindi' },
    { value: 'TELUGU', label: 'Telugu' },
    { value: 'MALAYALAM', label: 'Malayalam' },
  ],
  industry: [
    { value: 'SAAS', label: 'SaaS' },
    { value: 'ECOMMERCE', label: 'E-commerce' },
    { value: 'FINANCIAL_SERVICES', label: 'Financial Services' },
    { value: 'HOSPITAL', label: 'Hospital' },
    { value: 'MANUFACTURING', label: 'Manufacturing' },
    { value: 'RETAIL', label: 'Retail' },
    { value: 'LOGISTICS', label: 'Logistics' },
    { value: 'MEDICAL_DEVICES', label: 'Medical Devices' },
    { value: 'INSURANCE', label: 'Insurance' },
    { value: 'REAL_ESTATE', label: 'Real Estate' },
    { value: 'HOSPITALITY', label: 'Hospitality' },
    { value: 'AUTOMOTIVE', label: 'Automotive' },
    { value: 'UNIVERSAL', label: 'Universal' },
  ],
  companySize: [
    { value: 'RANGE_1_50', label: '1-50' },
    { value: 'RANGE_51_200', label: '51-200' },
    { value: 'RANGE_200_500', label: '200-500' },
    { value: 'RANGE_500_2000', label: '500-2000' },
    { value: 'RANGE_2000_PLUS', label: '2000+' },
  ],
  region: [
    { value: 'NORTH_AMERICA', label: 'North America' },
    { value: 'EMEA', label: 'EMEA' },
    { value: 'APAC', label: 'APAC' },
    { value: 'LATAM', label: 'LATAM' },
  ],
  relationshipType: [
    { value: 'NEW_PROSPECT', label: 'New Prospect' },
    { value: 'EXISTING_CUSTOMER', label: 'Existing Customer' },
    { value: 'CHURNED_CUSTOMER', label: 'Churned Customer' },
  ],
  buyerMood: [
    { value: 'CURIOUS', label: 'Curious' },
    { value: 'FRIENDLY', label: 'Friendly' },
    { value: 'OPEN_MINDED', label: 'Open-minded' },
    { value: 'SKEPTICAL', label: 'Skeptical' },
    { value: 'ANALYTICAL', label: 'Analytical' },
    { value: 'DEFENSIVE', label: 'Defensive' },
    { value: 'RESERVED_BUT_ENGAGED', label: 'Reserved but Engaged' },
    { value: 'BUSY', label: 'Busy' },
    { value: 'CAUTIOUS', label: 'Cautious' },
    { value: 'DISTRACTED', label: 'Distracted' },
    { value: 'RUSHED', label: 'Rushed' },
    { value: 'FRUSTRATED', label: 'Frustrated' },
    { value: 'AGGRESSIVE', label: 'Aggressive' },
    { value: 'DEMANDING', label: 'Demanding' },
    { value: 'IMPATIENT', label: 'Impatient' },
    { value: 'UNTRUSTING', label: 'Untrusting' },
    { value: 'POLITICALLY_CAREFUL', label: 'Politically Careful' },
  ],
  buyerPressure: [
    { value: 'BUDGET_PRESSURE', label: 'Budget Pressure' },
    { value: 'COMPETITIVE_EVALUATION', label: 'Competitive Evaluation' },
    { value: 'COMPETITIVE_THREAT', label: 'Competitive Threat' },
    { value: 'INTERNAL_DEADLINE', label: 'Internal Deadline' },
    { value: 'DEADLINE_CRUNCH', label: 'Deadline Crunch' },
    { value: 'PROCUREMENT_PROCESS', label: 'Procurement Process' },
    { value: 'TECHNICAL_VALIDATION', label: 'Technical Validation' },
    { value: 'LEADERSHIP_APPROVAL', label: 'Leadership Approval' },
    { value: 'EXISTING_VENDOR_CONTRACT', label: 'Existing Vendor Contract' },
    { value: 'RESOURCE_CONSTRAINTS', label: 'Resource Constraints' },
    { value: 'LEADERSHIP_CHANGE', label: 'Leadership Change' },
    { value: 'CHURN_RISK', label: 'Churn Risk' },
    { value: 'SECURITY_REVIEW', label: 'Security Review' },
    { value: 'LEGAL_REVIEW', label: 'Legal Review' },
    { value: 'ORGANIZATIONAL_RESTRUCTURING', label: 'Organizational Restructuring' },
    { value: 'BOARD_PRESSURE', label: 'Board Pressure' },
    { value: 'EXECUTIVE_MANDATE', label: 'Executive Mandate' },
    { value: 'ROI_SCRUTINY', label: 'ROI Scrutiny' },
    { value: 'CHANGE_RESISTANCE', label: 'Change Resistance' },
  ],
  communicationTone: [
    { value: 'NEUTRAL', label: 'Neutral' },
    { value: 'WARM', label: 'Warm' },
    { value: 'RUDE', label: 'Rude' },
  ],
  replyBehaviour: [
    { value: 'COOPERATIVE', label: 'Cooperative' },
    { value: 'NEEDS_CONVINCING', label: 'Needs Convincing' },
    { value: 'ACTIVELY_RESISTS', label: 'Actively Resists' },
  ],
  difficulty: [
    { value: 'EASY', label: 'Easy' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HARD', label: 'Hard' },
  ],
};

const DIFFICULTY_MOODS = {
  EASY: new Set(['CURIOUS', 'FRIENDLY', 'OPEN_MINDED', 'SKEPTICAL', 'ANALYTICAL']),
  MEDIUM: new Set(['SKEPTICAL', 'ANALYTICAL', 'DEFENSIVE', 'RESERVED_BUT_ENGAGED', 'CAUTIOUS', 'DISTRACTED', 'RUSHED']),
  HARD: new Set(['SKEPTICAL', 'ANALYTICAL', 'DEFENSIVE', 'RUSHED', 'FRUSTRATED', 'AGGRESSIVE', 'DEMANDING', 'IMPATIENT', 'UNTRUSTING', 'POLITICALLY_CAREFUL']),
};

const DIFFICULTY_PRESSURES = {
  EASY: new Set(['BUDGET_PRESSURE', 'COMPETITIVE_EVALUATION', 'INTERNAL_DEADLINE', 'PROCUREMENT_PROCESS', 'TECHNICAL_VALIDATION', 'RESOURCE_CONSTRAINTS', 'EXISTING_VENDOR_CONTRACT']),
  MEDIUM: new Set(['BUDGET_PRESSURE', 'COMPETITIVE_EVALUATION', 'INTERNAL_DEADLINE', 'PROCUREMENT_PROCESS', 'TECHNICAL_VALIDATION', 'LEADERSHIP_APPROVAL', 'EXISTING_VENDOR_CONTRACT', 'RESOURCE_CONSTRAINTS']),
  HARD: new Set(['BUDGET_PRESSURE', 'COMPETITIVE_EVALUATION', 'INTERNAL_DEADLINE', 'PROCUREMENT_PROCESS', 'TECHNICAL_VALIDATION', 'LEADERSHIP_APPROVAL', 'EXISTING_VENDOR_CONTRACT', 'LEADERSHIP_CHANGE', 'CHURN_RISK', 'SECURITY_REVIEW', 'LEGAL_REVIEW', 'ORGANIZATIONAL_RESTRUCTURING', 'EXECUTIVE_MANDATE', 'ROI_SCRUTINY', 'CHANGE_RESISTANCE']),
};

const getMoodOptions = (difficulty) => {
  if (!difficulty || !DIFFICULTY_MOODS[difficulty]) return ENUM_OPTIONS.buyerMood;
  const allowed = DIFFICULTY_MOODS[difficulty];
  return ENUM_OPTIONS.buyerMood.filter(opt => allowed.has(opt.value));
};

const getPressureOptions = (difficulty) => {
  if (!difficulty || !DIFFICULTY_PRESSURES[difficulty]) return ENUM_OPTIONS.buyerPressure;
  const allowed = DIFFICULTY_PRESSURES[difficulty];
  return ENUM_OPTIONS.buyerPressure.filter(opt => allowed.has(opt.value));
};

const normalizeDifficulty = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'number') {
    return ['EASY', 'MEDIUM', 'HARD'][value] ?? '';
  }
  const normalized = String(value).trim().toUpperCase();
  return ENUM_OPTIONS.difficulty.some((option) => option.value === normalized) ? normalized : '';
};

const FormLabel = ({ children, required }) => (
  <label className="block text-left mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const inputBaseClass =
  'w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 shadow-sm transition-[box-shadow,border-color] focus:outline-none focus:border-[#0842A0] focus:ring-4 focus:ring-[#0842A0]/10';

const CustomInput = ({ type = "text", ...props }) => {
  if (type === "textarea") {
    return <textarea {...props} className={`${inputBaseClass} resize-y min-h-[88px]`} />;
  }
  if (type === "select") {
    return <select {...props} className={`${inputBaseClass} cursor-pointer`} />;
  }
  return <input type={type} {...props} className={inputBaseClass} />;
};
const ActivePill = ({ text, onRemove, imageUrl, subtitle }) => (
  <span className="inline-flex flex-col gap-0.5 max-w-[min(100%,260px)] pl-3 pr-1.5 py-1.5 rounded-lg text-sm text-slate-700 bg-slate-100 border border-slate-200/80">
    <span className="inline-flex items-center gap-1.5 min-w-0">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={text}
          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
          loading="lazy"
        />
      ) : null}
      <span className="min-w-0 truncate flex-1">{text}</span>
      <button
        type="button"
        onClick={onRemove}
        className="p-1 rounded-md text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0"
        aria-label="Remove"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </span>
    {subtitle ? (
      <span className="text-[10px] text-slate-500 truncate pr-6" title={subtitle}>
        Skill: {subtitle}
      </span>
    ) : null}
  </span>
);

const SuggestionPill = ({ text, onAdd, imageUrl, subtitle }) => (
  <button
    type="button"
    onClick={() => onAdd(text)}
    className="inline-flex flex-col items-stretch gap-0.5 max-w-[min(100%,260px)] pl-3 pr-2 py-1.5 rounded-lg text-sm font-medium text-slate-600 bg-white border border-slate-200 shadow-sm hover:border-slate-300 hover:bg-slate-50 transition-colors text-left"
  >
    <span className="inline-flex items-center gap-1.5 min-w-0">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={text}
          className="w-5 h-5 rounded-full object-cover flex-shrink-0"
          loading="lazy"
        />
      ) : null}
      <span className="truncate flex-1">{text}</span>
      <Plus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
    </span>
    {subtitle ? (
      <span className="text-[10px] text-slate-500 font-normal truncate pr-1" title={subtitle}>
        Skill: {subtitle}
      </span>
    ) : null}
  </button>
);

const RolePlayOption = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border text-left transition-all min-w-[132px] ${
      selected
        ? 'border-[#0842A0] bg-[#f0f6ff] shadow-sm ring-1 ring-[#0842A0]/20'
        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
    }`}
  >
    <div
      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
        selected ? 'border-[#0842A0]' : 'border-slate-300'
      }`}
    >
      {selected && <div className="w-2 h-2 bg-[#0842A0] rounded-full" />}
    </div>
    <span className="text-sm font-medium text-slate-900">{label}</span>
  </button>
);

const InteractionToggle = ({ options, value, onChange }) => (
  <div className="inline-flex p-1 rounded-xl border border-slate-200 bg-slate-100/80 w-full max-w-md">
    {options.map((opt) => (
      <button
        type="button"
        key={opt}
        onClick={() => onChange(opt)}
        className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1 ${
          value === opt
            ? 'bg-[#0842A0] text-white shadow-sm'
            : 'text-slate-700 hover:bg-white/80'
        }`}
      >
        <span>{opt}</span>
        <span className="text-[10px] sm:text-xs uppercase tracking-wide opacity-90">min</span>
      </button>
    ))}
  </div>
);

const TONE_COLORS = {
  NEUTRAL: 'bg-blue-50 text-blue-700 border-blue-200',
  WARM: 'bg-amber-50 text-amber-700 border-amber-200',
  RUDE: 'bg-rose-50 text-rose-700 border-rose-200',
};

const REPLY_COLORS = {
  COOPERATIVE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  NEEDS_CONVINCING: 'bg-orange-50 text-orange-700 border-orange-200',
  ACTIVELY_RESISTS: 'bg-red-50 text-red-700 border-red-200',
};

const MOOD_COLORS = {
  CURIOUS: 'bg-teal-50 text-teal-700 border-teal-200',
  FRIENDLY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  OPEN_MINDED: 'bg-green-50 text-green-700 border-green-200',
  SKEPTICAL: 'bg-purple-50 text-purple-700 border-purple-200',
  ANALYTICAL: 'bg-blue-50 text-blue-700 border-blue-200',
  DEFENSIVE: 'bg-amber-50 text-amber-700 border-amber-200',
  RESERVED_BUT_ENGAGED: 'bg-sky-50 text-sky-700 border-sky-200',
  BUSY: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  CAUTIOUS: 'bg-slate-50 text-slate-700 border-slate-200',
  DISTRACTED: 'bg-gray-50 text-gray-700 border-gray-200',
  RUSHED: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  FRUSTRATED: 'bg-red-50 text-red-700 border-red-200',
  AGGRESSIVE: 'bg-orange-50 text-orange-700 border-orange-200',
  DEMANDING: 'bg-rose-50 text-rose-700 border-rose-200',
  IMPATIENT: 'bg-amber-50 text-amber-700 border-amber-200',
  UNTRUSTING: 'bg-stone-50 text-stone-700 border-stone-200',
  POLITICALLY_CAREFUL: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

const PRESSURE_COLORS = {
  BUDGET_PRESSURE: 'bg-pink-50 text-pink-700 border-pink-200',
  COMPETITIVE_EVALUATION: 'bg-violet-50 text-violet-700 border-violet-200',
  COMPETITIVE_THREAT: 'bg-violet-50 text-violet-700 border-violet-200',
  INTERNAL_DEADLINE: 'bg-orange-50 text-orange-700 border-orange-200',
  DEADLINE_CRUNCH: 'bg-orange-50 text-orange-700 border-orange-200',
  PROCUREMENT_PROCESS: 'bg-blue-50 text-blue-700 border-blue-200',
  TECHNICAL_VALIDATION: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  LEADERSHIP_APPROVAL: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  EXISTING_VENDOR_CONTRACT: 'bg-amber-50 text-amber-700 border-amber-200',
  RESOURCE_CONSTRAINTS: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  LEADERSHIP_CHANGE: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  CHURN_RISK: 'bg-rose-50 text-rose-700 border-rose-200',
  SECURITY_REVIEW: 'bg-red-50 text-red-700 border-red-200',
  LEGAL_REVIEW: 'bg-stone-50 text-stone-700 border-stone-200',
  ORGANIZATIONAL_RESTRUCTURING: 'bg-purple-50 text-purple-700 border-purple-200',
  BOARD_PRESSURE: 'bg-red-50 text-red-700 border-red-200',
  EXECUTIVE_MANDATE: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  ROI_SCRUTINY: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  CHANGE_RESISTANCE: 'bg-amber-50 text-amber-700 border-amber-200',
};

const labelForEnum = (list, value) =>
  value != null && String(value).trim() !== ''
    ? list?.find((o) => o.value === value)?.label ?? value
    : null;
const emptyStrToNull = (v) => {
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
};

const getPersonaDisplayKey = (persona) => {
  if (persona?.label != null && String(persona.label).trim() !== '') {
    return String(persona.label).trim();
  }
  if (persona?.name != null && String(persona.name).trim() !== '') {
    return String(persona.name).trim();
  }
  return '';
};

const resolvePersonaId = (personaKey, personas) => {
  const persona = personas.find((p) => getPersonaDisplayKey(p) === personaKey);
  const rawId = persona?.id ?? persona?.learnerRoleId;
  if (rawId == null) return null;
  const numericId = Number(rawId);
  return Number.isFinite(numericId) ? numericId : null;
};

const MOOD_DETECT_PATTERNS = [
  { key: 'SKEPTICAL', patterns: [/\b(doubt|skeptic|questioning|proof|evidence|distrust|cautious|not sure|not convinced|show me|prove it|hard to convince|needs proof|wants evidence|doesn't trust|not buying it|show the numbers|unconvinced)/i] },
  { key: 'FRUSTRATED', patterns: [/\b(frustrated|annoyed|impatient|dissatisfied|unhappy|complaint|sigh|waste|not happy|bad experience|rude|impolite|agitated|upset|fed up|tired of|sick of|blowing up|short tempered)/i] },
  { key: 'DEFENSIVE', patterns: [/\b(defend|current vendor|status quo|not change|pushing back|resist|protective|works fine|wedded to|sticking with|loyal to|happy as is|why change|if it ain't broke|satisfied with what we have)/i] },
  { key: 'RUSHED', patterns: [/\b(rush|interrupt|busy|quick|short on time|limited time|time-sensitive|clock|bottom line|get to the point|no time|in a hurry|running late|pressed for time|can't talk long|make it quick)/i] },
  { key: 'CURIOUS', patterns: [/\b(curious|eager to learn|wants to understand|exploring|new to this|interested|wants to know more|open to learning)/i] },
  { key: 'FRIENDLY', patterns: [/\b(friendly|warm|polite|nice|pleasant|cooperative|cordial|approachable|easygoing)/i] },
  { key: 'OPEN_MINDED', patterns: [/\b(open minded|open to suggestions|willing to consider|receptive|flexible|not set in their ways)/i] },
  { key: 'ANALYTICAL', patterns: [/\b(analytical|data driven|detail oriented|wants specifics|numbers person|logical|methodical|wants data|research)/i] },
  { key: 'RESERVED_BUT_ENGAGED', patterns: [/\b(reserved but engaged|measured|guarded but interested|listening carefully|participating cautiously|engaged but cautious)/i] },
  { key: 'AGGRESSIVE', patterns: [/\b(aggressive|hostile|confrontational|pushy|forceful|in your face|intimidating|combative)/i] },
  { key: 'DEMANDING', patterns: [/\b(demanding|high expectations|expects the best|won't settle|perfectionist|exacting|unyielding)/i] },
  { key: 'DISTRACTED', patterns: [/\b(distracted|multitasking|pulled in different directions|not paying attention|unfocused|preoccupied|divided attention)/i] },
  { key: 'UNTRUSTING', patterns: [/\b(untrusting|suspicious|distrustful|cynical|doubts intentions|doesn't believe|paranoid|wary)/i] },
  { key: 'POLITICALLY_CAREFUL', patterns: [/\b(politically careful|navigating politics|cautious about commitments|defers decisions|checks stakeholders|careful phrasing)/i] },
];

const PRESSURE_DETECT_PATTERNS = [
  { key: 'BUDGET_PRESSURE', patterns: [/\b(budget|cost|spend|pric|expensive|ROI|value|dollar|financial|afford|funding|tight budget|no money|underfunded|cut costs|reduce spending|can't afford|stretched thin)/i] },
  { key: 'COMPETITIVE_EVALUATION', patterns: [/\b(compare|evaluat|weighing|shopping|options|alternatives|choose|decide|not yet committed|window shopping|kicking tires|testing the waters|not ready to commit|still looking|exploring options)/i] },
  { key: 'DEADLINE_CRUNCH', patterns: [/\b(deadline|timeline|urgent|time.sensitive|quick|immediate|coming up|due soon|running out of time|clock is ticking|last minute|crunched for time)/i] },
  { key: 'COMPETITIVE_THREAT', patterns: [/\b(compet|rival|market share|other vendor|alternative|switching|stealing business|losing ground|being undercut)/i] },
  { key: 'CHURN_RISK', patterns: [/\b(churn|retention|renew|cancel|leaving|disconnect|discontinue|switch|risk|loss|thinking of leaving|not happy with|looking elsewhere|second thoughts|on the fence|about to leave)/i] },
  { key: 'LEADERSHIP_CHANGE', patterns: [/\b(reorg|new leadership|new executive|strategy shift|restructur|new management|new CEO|change in direction|new boss|leadership shakeup)/i] },
  { key: 'RESOURCE_CONSTRAINTS', patterns: [/\b(bandwidth|headcount|capacity|priorities|competing|limited|stretched|thin|not enough people|understaffed|too much on their plate)/i] },
  { key: 'SECURITY_REVIEW', patterns: [/\b(security|compliance|audit|data privacy|risk assessment|certification|encryption|vulnerability|data breach|regulatory)/i] },
  { key: 'INTERNAL_DEADLINE', patterns: [/\b(quarterly|milestone|internal review|board meeting|reporting|deadline|fiscal year|end of quarter|end of month)/i] },
  { key: 'CHANGE_RESISTANCE', patterns: [/\b(change management|adoption|resistance|inertia|cultural|barrier|averse to change|set in their ways|won't adopt new)/i] },
  { key: 'ROI_SCRUTINY', patterns: [/\b(ROI justification|business case|cost.benefit|value demonstration|justify|prove the value|show the return|worth the investment)/i] },
  { key: 'BOARD_PRESSURE', patterns: [/\b(board|investor|stakeholder|board directive|shareholder|investor pressure)/i] },
  { key: 'EXECUTIVE_MANDATE', patterns: [/\b(executive mandate|leadership mandate|leadership directive|C-suite directive|CEO directive|executive priority|mandate from leadership)/i] },
];

const detectMoodFromText = (text) => {
  if (!text) return '';
  for (const { key, patterns } of MOOD_DETECT_PATTERNS) {
    if (patterns.some(p => p.test(text))) return key;
  }
  return '';
};

const detectPressureFromText = (text) => {
  if (!text) return '';
  for (const { key, patterns } of PRESSURE_DETECT_PATTERNS) {
    if (patterns.some(p => p.test(text))) return key;
  }
  return '';
};

const enumOrNull = (v) => (v != null && String(v).trim() !== '' ? v : null);

const composeBuyerStatePrompt = (mood, pressure) => {
  const moodPrompt = mood ? BUYER_MOOD_PROMPTS[mood] || '' : '';
  const pressurePrompt = pressure ? BUYER_PRESSURE_PROMPTS[pressure] || '' : '';
  return [moodPrompt, pressurePrompt].filter(Boolean).join('\n\n');
};

/** Filled when user sets company name or any company-context enum; fallbacks for unset enums. */
const buildGenericCompanyContextPrompt = ({
  companyName,
  industry,
  companySize,
  region,
  relationshipType,
  language,
}) => {
  const industryLabel =
    labelForEnum(ENUM_OPTIONS.industry, industry) || 'the relevant industry';
  const companySizeLabel =
    labelForEnum(ENUM_OPTIONS.companySize, companySize) || 'an unspecified';
  const regionLabel = labelForEnum(ENUM_OPTIONS.region, region) || 'the relevant region';
  const languageLabel = labelForEnum(ENUM_OPTIONS.language, language) || 'English';
  const rt = labelForEnum(ENUM_OPTIONS.relationshipType, relationshipType);
  const rtLower = rt ? rt.toLowerCase() : '';
  const relArticle =
    rtLower && /^[aeiou]/i.test(rtLower) ? 'an' : rt ? 'a' : 'a';
  const relPart = rt ? `${relArticle} ${rtLower} ` : 'a ';
  const companyText =
    companyName != null && String(companyName).trim() !== ''
      ? String(companyName).trim()
      : `a ${industryLabel} company`;

  return `You are speaking with ${relPart}buyer at ${companyText} with ${companySizeLabel} employees in ${regionLabel}. Keep the scenario realistic for this market context, and respond in ${languageLabel}.`;
};

/** Right-hand live summary of the scenario being configured */
const ScenarioLivePreview = ({
  title,
  description,
  coverImageUrl,
  imageS3Key,
  imagePreviewUrl,
  imagePreviewS3Key,
  videoPreviewUrl,
  videoPreviewS3Key,
  mode,
  language,
  industry,
  companySize,
  region,
  relationshipType,
  difficulty,
  buyerMood,
  onBuyerMoodChange,
  buyerPressure,
  onBuyerPressureChange,
  communicationTone,
  onCommunicationToneChange,
  replyBehaviour,
  onReplyBehaviourChange,
  buyerStatePrompt,
  onBuyerStatePromptChange,
  communicationTonePrompt,
  onCommunicationTonePromptChange,
  replyBehaviourPrompt,
  onReplyBehaviourPromptChange,
  companyContextPrompt,
  callArcPrompt,
  customAgentRules,
  onCustomAgentRulesChange,
  openingMessage,
  conversationPrompts,
  selectedTagPreviewItems,
  skillNamesFromTags,
  skillWeightages,
  totalWeightage,
  rolePlayType,
  recommended,
  interactionTime,
  maxAttempts,
  interruptLogicPrompt,
  coachFlowPrompt,
  selectedCoachName,
  objections,
}) => {
  const hasCompanyContext =
    companyContextPrompt != null && String(companyContextPrompt).trim() !== '';
  const hasCustomRules = customAgentRules != null && String(customAgentRules).trim() !== '';
  const [editingBuyerState, setEditingBuyerState] = useState(false);
  const [editingTonePrompt, setEditingTonePrompt] = useState(false);
  const [editingReplyPrompt, setEditingReplyPrompt] = useState(false);
  const [editingCustomRules, setEditingCustomRules] = useState(false);
  const hasBuyerState = buyerStatePrompt != null && String(buyerStatePrompt).trim() !== '';
  const hasOpening = openingMessage != null && String(openingMessage).trim() !== '';
  const hasAnyEnumContext =
    (language != null && String(language).trim() !== '') ||
    (industry != null && String(industry).trim() !== '') ||
    (companySize != null && String(companySize).trim() !== '') ||
    (region != null && String(region).trim() !== '') ||
    (relationshipType != null && String(relationshipType).trim() !== '');
  const hasMoodPills = true;
  return (
  <div className="rounded-2xl border border-slate-200/90 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.06)] overflow-hidden">
    <div className="p-5 space-y-5 text-left text-sm">
      {(coverImageUrl || imageS3Key) && (
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/9] max-h-36">
          <img
            src={coverImageUrl || getS3Url(imageS3Key)}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}
      {(imagePreviewUrl || imagePreviewS3Key) && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Image preview</div>
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/9] max-h-28">
            <img
              src={imagePreviewUrl || getS3Url(imagePreviewS3Key)}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}
      {(videoPreviewUrl || videoPreviewS3Key) && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Video preview</div>
          <div className="rounded-xl overflow-hidden border border-slate-200 bg-black aspect-[16/9] max-h-28">
            <video
              src={videoPreviewUrl || getS3Url(videoPreviewS3Key)}
              controls
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      )}
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Title</div>
        <div className="font-semibold text-slate-900 leading-snug">{title || '—'}</div>
      </div>
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Description</div>
        <p className="text-slate-600 text-[13px] leading-relaxed whitespace-pre-wrap line-clamp-6">
          {description || '—'}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-3 text-[13px]">
        {labelForEnum(ENUM_OPTIONS.mode, mode) != null && (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Mode</span>
            <span className="text-slate-800">{labelForEnum(ENUM_OPTIONS.mode, mode)}</span>
          </div>
        )}
        {selectedCoachName ? (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Coach</span>
            <span className="text-slate-800">{selectedCoachName}</span>
          </div>
        ) : null}
        {labelForEnum(ENUM_OPTIONS.language, language) != null && (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Language</span>
            <span className="text-slate-800">{labelForEnum(ENUM_OPTIONS.language, language)}</span>
          </div>
        )}
        {rolePlayType != null && String(rolePlayType).trim() !== '' && (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Visibility</span>
            <span className="text-slate-800">{rolePlayType}</span>
          </div>
        )}
        {recommended === true || recommended === false ? (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Recommended</span>
            <span className="text-slate-800">{recommended ? 'Yes' : 'No'}</span>
          </div>
        ) : null}
        {labelForEnum(ENUM_OPTIONS.difficulty, difficulty) != null && (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Difficulty</span>
            <span className="text-slate-800">{labelForEnum(ENUM_OPTIONS.difficulty, difficulty)}</span>
          </div>
        )}
        {interactionTime != null && String(interactionTime).trim() !== '' && (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Duration</span>
            <span className="text-slate-800">{interactionTime} min</span>
          </div>
        )}
        {maxAttempts !== '' && maxAttempts != null && (
          <div>
            <span className="text-slate-400 block text-[11px] uppercase font-semibold tracking-wide">Max attempts</span>
            <span className="text-slate-800">{maxAttempts}</span>
          </div>
        )}
      </div>
      {hasAnyEnumContext && (
        <div className="grid grid-cols-1 gap-2 text-[13px]">
          {(labelForEnum(ENUM_OPTIONS.industry, industry) != null ||
            labelForEnum(ENUM_OPTIONS.companySize, companySize) != null ||
            labelForEnum(ENUM_OPTIONS.region, region) != null) && (
            <div>
              <span className="text-slate-400 text-[11px] uppercase font-semibold tracking-wide">Industry · Size · Region</span>
              <div className="text-slate-800">
                {[labelForEnum(ENUM_OPTIONS.industry, industry), labelForEnum(ENUM_OPTIONS.companySize, companySize), labelForEnum(ENUM_OPTIONS.region, region)]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </div>
          )}
          {labelForEnum(ENUM_OPTIONS.relationshipType, relationshipType) != null && (
            <div>
              <span className="text-slate-400 text-[11px] uppercase font-semibold tracking-wide">Relationship</span>
              <div className="text-slate-800">{labelForEnum(ENUM_OPTIONS.relationshipType, relationshipType)}</div>
            </div>
          )}
        </div>
      )}
      {hasMoodPills && (
        <div className="flex flex-wrap gap-2">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${MOOD_COLORS[buyerMood] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
              Mood:
              <select
                value={buyerMood || ''}
                onChange={(e) => onBuyerMoodChange(e.target.value)}
                className="bg-transparent border-none text-xs font-medium focus:outline-none"
                style={{ color: 'inherit' }}
              >
                <option value="">Select…</option>
                {getMoodOptions(difficulty).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </span>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${PRESSURE_COLORS[buyerPressure] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
              Pressure:
              <select
                value={buyerPressure || ''}
                onChange={(e) => onBuyerPressureChange(e.target.value)}
                className="bg-transparent border-none text-xs font-medium focus:outline-none"
                style={{ color: 'inherit' }}
              >
                <option value="">Select…</option>
                {getPressureOptions(difficulty).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </span>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${TONE_COLORS[communicationTone] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
              Tone:
              <select
                value={communicationTone || ''}
                onChange={(e) => onCommunicationToneChange(e.target.value)}
                className="bg-transparent border-none text-xs font-medium focus:outline-none"
                style={{ color: 'inherit' }}
              >
                <option value="">Select…</option>
                {ENUM_OPTIONS.communicationTone.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </span>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${REPLY_COLORS[replyBehaviour] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
              Reply:
              <select
                value={replyBehaviour || ''}
                onChange={(e) => onReplyBehaviourChange(e.target.value)}
                className="bg-transparent border-none text-xs font-medium focus:outline-none"
                style={{ color: 'inherit' }}
              >
                <option value="">Select…</option>
                {ENUM_OPTIONS.replyBehaviour.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </span>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Buyer state prompt</div>
          <div className="flex items-center gap-1">
            {editingBuyerState && (
              <button
                type="button"
                onClick={() => setEditingBuyerState(false)}
                className="text-slate-400 hover:text-green-600 transition-colors"
              >
                <Save size={14} />
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditingBuyerState(!editingBuyerState)}
              className="text-slate-400 hover:text-[#0842A0] transition-colors"
            >
              <Pen size={14} />
            </button>

          </div>
        </div>
        {editingBuyerState ? (
          <textarea
            className="w-full text-slate-600 text-[12px] leading-relaxed p-2 border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-[#0842A0]"
            rows={3}
            value={buyerStatePrompt}
            onChange={(e) => onBuyerStatePromptChange(e.target.value)}
            placeholder="Buyer state prompt..."
          />
        ) : (
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap">{buyerStatePrompt || '—'}</p>
        )}
      </div>
      {communicationTonePrompt != null && String(communicationTonePrompt).trim() !== '' && (
      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Communication tone prompt</div>
          <div className="flex items-center gap-1">
            {editingTonePrompt && (
              <button type="button" onClick={() => setEditingTonePrompt(false)} className="text-slate-400 hover:text-green-600 transition-colors">
                <Save size={14} />
              </button>
            )}
            <button type="button" onClick={() => setEditingTonePrompt(!editingTonePrompt)} className="text-slate-400 hover:text-[#0842A0] transition-colors">
              <Pen size={14} />
            </button>
          </div>
        </div>
        {editingTonePrompt ? (
          <textarea
            className="w-full text-slate-600 text-[12px] leading-relaxed p-2 border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-[#0842A0]"
            rows={3}
            value={communicationTonePrompt}
            onChange={(e) => onCommunicationTonePromptChange(e.target.value)}
            placeholder="Communication tone prompt..."
          />
        ) : (
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap">{communicationTonePrompt}</p>
        )}
      </div>
      )}
      {replyBehaviourPrompt != null && String(replyBehaviourPrompt).trim() !== '' && (
      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Reply behaviour prompt</div>
          <div className="flex items-center gap-1">
            {editingReplyPrompt && (
              <button type="button" onClick={() => setEditingReplyPrompt(false)} className="text-slate-400 hover:text-green-600 transition-colors">
                <Save size={14} />
              </button>
            )}
            <button type="button" onClick={() => setEditingReplyPrompt(!editingReplyPrompt)} className="text-slate-400 hover:text-[#0842A0] transition-colors">
              <Pen size={14} />
            </button>
          </div>
        </div>
        {editingReplyPrompt ? (
          <textarea
            className="w-full text-slate-600 text-[12px] leading-relaxed p-2 border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-[#0842A0]"
            rows={3}
            value={replyBehaviourPrompt}
            onChange={(e) => onReplyBehaviourPromptChange(e.target.value)}
            placeholder="Reply behaviour prompt..."
          />
        ) : (
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap">{replyBehaviourPrompt}</p>
        )}
      </div>
      )}
      {hasOpening && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Opening line</div>
          <p className="text-slate-700 text-[13px] leading-relaxed whitespace-pre-wrap line-clamp-4">{openingMessage}</p>
        </div>
      )}
      {conversationPrompts.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Starter prompts</div>
          <ul className="list-disc list-inside text-slate-600 text-[12px] space-y-1">
            {conversationPrompts.slice(0, 6).map((p, i) => (
              <li key={i} className="line-clamp-2">
                {p}
              </li>
            ))}
          </ul>
        </div>
      )}
      {objections && objections.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Objections</div>
          <ul className="list-disc list-inside text-slate-600 text-[12px] space-y-1">
            {objections.slice(0, 6).map((o, i) => (
              <li key={i} className="line-clamp-2">{o}</li>
            ))}
          </ul>
        </div>
      )}
      {(skillNamesFromTags.length > 0 || selectedTagPreviewItems.length > 0) && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
            Selected skills, weightage & tags <span className="text-slate-500 font-normal">(total {totalWeightage})</span>
          </div>
          <ul className="space-y-1.5 text-[12px] mb-3">
            {skillNamesFromTags.map((sk) => {
              return (
                <li key={sk} className="flex justify-between gap-2">
                  <div className="font-medium text-slate-800 truncate">{sk}</div>
                  <span className="shrink-0 text-slate-600 tabular-nums">{skillWeightages[sk] ?? '—'}%</span>
                </li>
              );
            })}
          </ul>
          {selectedTagPreviewItems.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {selectedTagPreviewItems.map(({ id, label }) => (
                <span
                  key={id}
                  className="px-2 py-0.5 rounded-md bg-[#f0f6ff] text-[#0842A0] text-xs font-medium border border-[#0842A0]/15"
                >
                  {label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {hasCompanyContext && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Company context</div>
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap line-clamp-3">{companyContextPrompt}</p>
        </div>
      )}
      {callArcPrompt != null && String(callArcPrompt).trim() !== '' && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Call flow</div>
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap line-clamp-3">{callArcPrompt}</p>
        </div>
      )}
      <div>
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Scenario rules</div>
          <div className="flex items-center gap-1">
            {editingCustomRules && (
              <button type="button" onClick={() => setEditingCustomRules(false)} className="text-slate-400 hover:text-green-600 transition-colors">
                <Save size={14} />
              </button>
            )}
            <button type="button" onClick={() => setEditingCustomRules(!editingCustomRules)} className="text-slate-400 hover:text-[#0842A0] transition-colors">
              <Pen size={14} />
            </button>
          </div>
        </div>
        {editingCustomRules || !hasCustomRules ? (
          <textarea
            className="w-full text-slate-600 text-[12px] leading-relaxed p-2 border border-slate-200 rounded-md resize-y focus:outline-none focus:ring-1 focus:ring-[#0842A0]"
            rows={3}
            value={customAgentRules || ''}
            onChange={(e) => onCustomAgentRulesChange(e.target.value)}
            placeholder="Add scenario rules..."
          />
        ) : (
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap">{customAgentRules}</p>
        )}
      </div>
      {mode === 'COACH' && interruptLogicPrompt != null && String(interruptLogicPrompt).trim() !== '' && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Interrupt logic</div>
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap line-clamp-3">{interruptLogicPrompt}</p>
        </div>
      )}
      {mode === 'COACH' && coachFlowPrompt != null && String(coachFlowPrompt).trim() !== '' && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Coach flow</div>
          <p className="text-slate-600 text-[12px] leading-relaxed whitespace-pre-wrap line-clamp-3">{coachFlowPrompt}</p>
        </div>
      )}
    </div>
  </div>
  );
};

const CoachingScenarioSetup = () => {
  useAuthToken();
  const { selectedVendorId, selectedVendor, isPublic } = useVendor();
  const vendorLabel = isPublic
    ? 'Public'
    : selectedVendor?.name || (selectedVendorId != null ? `Vendor #${selectedVendorId}` : 'Public');
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const imagePreviewInputRef = useRef(null);
  const videoPreviewInputRef = useRef(null);
  const [searchParams] = useSearchParams();
  const { scenarioId: scenarioIdFromPath } = useParams();
  const editingScenarioId = scenarioIdFromPath || searchParams.get('scenarioId');

  // Loading States
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingText, setLoadingText] = useState('');

  // States
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [rolePlayType, setRolePlayType] = useState('');
  const [recommended, setRecommended] = useState(null);
  const [archived, setArchived] = useState(false);
  const [published, setPublished] = useState(true);
  const [difficulty, setDifficulty] = useState('');
  const [mode, setMode] = useState('SALES');
  const [interactionTime, setInteractionTime] = useState('3');
  const [maxAttempts, setMaxAttempts] = useState('3');
  const [language, setLanguage] = useState('');
  const [industry, setIndustry] = useState('');
  const [companySize, setCompanySize] = useState('');
  const [region, setRegion] = useState('');
  const [relationshipType, setRelationshipType] = useState('');
  const [buyerMood, setBuyerMood] = useState('');
  const [buyerPressure, setBuyerPressure] = useState('');
  const [showRefinementPicker, setShowRefinementPicker] = useState(false);
  const [refinementsData, setRefinementsData] = useState(null);
  const [refinementsLoading, setRefinementsLoading] = useState(false);

  const [communicationTone, setCommunicationTone] = useState('');
  const [replyBehaviour, setReplyBehaviour] = useState('');
  const [buyerStatePrompt, setBuyerStatePrompt] = useState('');
  const [communicationTonePrompt, setCommunicationTonePrompt] = useState('');
  const [replyBehaviourPrompt, setReplyBehaviourPrompt] = useState('');
  const [callArcPrompt, setCallArcPrompt] = useState(CALL_FLOW_PROMPT_DEFAULT);
  const [isCallFlowEditable, setIsCallFlowEditable] = useState(false);

  const [customAgentRules, setCustomAgentRules] = useState('');
  const [companyContextPrompt, setCompanyContextPrompt] = useState('');
  const [isCompanyContextPromptManuallyEdited, setIsCompanyContextPromptManuallyEdited] =
    useState(false);
  const [interruptLogicPrompt, setInterruptLogicPrompt] = useState('');
  const [coachFlowPrompt, setCoachFlowPrompt] = useState('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [openingMessage, setOpeningMessage] = useState('');

  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [personaMatchFailed, setPersonaMatchFailed] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [files, setFiles] = useState([]);
  /** Saved scenario attachments (edit mode); only rows with a non-empty s3Key are shown and re-sent on save. */
  const [existingAttachments, setExistingAttachments] = useState([]);
  /** S3 object key from `/admin/roleplay-scenarios/image/upload-url` after PUT to presigned URL */
  const [imageS3Key, setImageS3Key] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [coverImageUploading, setCoverImageUploading] = useState(false);
  const [imagePreviewS3Key, setImagePreviewS3Key] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState('');
  const [imagePreviewUploading, setImagePreviewUploading] = useState(false);
  const [videoPreviewS3Key, setVideoPreviewS3Key] = useState(null);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState('');
  const [videoPreviewUploading, setVideoPreviewUploading] = useState(false);

  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiExpanded, setAiExpanded] = useState(true);
  const [showVagueSuggestion, setShowVagueSuggestion] = useState(false);
  const [hasAiGeneratedOnce, setHasAiGeneratedOnce] = useState(false);

  const [promptInput, setPromptInput] = useState('');
  const [conversationPrompts, setConversationPrompts] = useState([]);

  const [objections, setObjections] = useState([]);
  const [objectionInput, setObjectionInput] = useState('');
  const [objectionRegenLoading, setObjectionRegenLoading] = useState(false);
  const [convPromptRegenLoading, setConvPromptRegenLoading] = useState(false);
  const [behaviorPromptsLoading, setBehaviorPromptsLoading] = useState(false);

  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [scenarioTagsSnapshot, setScenarioTagsSnapshot] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [selectedSkillNames, setSelectedSkillNames] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [skillWeightages, setSkillWeightages] = useState({});
  const scenarioSkillWeightagesNullRef = useRef(false);
  const didInitSkillWeightsRef = useRef(false);
  const isHydratingScenarioRef = useRef(false);

  const mergedTags = useMemo(() => {
    const byId = new Map();
    [...availableTags, ...scenarioTagsSnapshot].forEach((tag) => {
      if (tag?.id == null) return;
      const nid = Number(tag.id);
      if (!Number.isFinite(nid)) return;
      byId.set(nid, tag);
    });
    return [...byId.values()];
  }, [availableTags, scenarioTagsSnapshot]);

  const findTagById = useCallback(
    (id) => mergedTags.find((t) => Number(t.id) === Number(id)),
    [mergedTags]
  );

  const fetchTagsForIndustry = useCallback(async (industryValue) => {
    if (!industryValue) {
      setAvailableTags([]);
      return [];
    }

    setTagsLoading(true);
    try {
      const seen = new Set();
      const merged = [];
      let page = 0;
      const perPage = 100;

      while (true) {
        const tagsRes = await api.getRoleplayTags({
          page,
          perPage,
          industry: industryValue,
          ...(selectedVendorId != null ? { vendorId: selectedVendorId } : {}),
        });
        const envelope = tagsRes.data?.data;
        const maybeList = envelope?.data ?? tagsRes.data?.data?.data ?? tagsRes.data?.data ?? [];
        const pageRows = Array.isArray(maybeList) ? maybeList : [];

        pageRows.forEach((row) => {
          const key = row?.id != null ? `id:${row.id}` : `lbl:${row?.label || row?.name || ''}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(row);
          }
        });

        if (pageRows.length === 0 || pageRows.length < perPage) break;
        page += 1;
      }

      setAvailableTags(merged);
      return merged;
    } catch (err) {
      console.error('Error fetching roleplay tags:', err);
      setAvailableTags([]);
      return [];
    } finally {
      setTagsLoading(false);
    }
  }, [selectedVendorId]);

  useEffect(() => {
    fetchTagsForIndustry(industry);
  }, [industry, fetchTagsForIndustry]);

  useEffect(() => {
    if (!editingScenarioId) {
      isHydratingScenarioRef.current = false;
      return;
    }
    if (!tagsLoading) {
      isHydratingScenarioRef.current = false;
    }
  }, [editingScenarioId, tagsLoading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let personasList = [];
        const vendorParams = selectedVendorId != null ? { vendorId: selectedVendorId } : {};
        const personasRes = await api.getRoleplayLearners(vendorParams);
        if (personasRes.data.success) {
          personasList = personasRes.data.data.data;
          setAvailablePersonas(personasList);
        }

        let agentsList = [];
        const coachesRes = await api.getRoleplayAgents(vendorParams);
        if (coachesRes.data.success) {
          agentsList = coachesRes.data.data?.data ?? [];
          const fetchedCoaches = agentsList.map((item) => ({
            id: item.id,
            name: item.name,
            subtitle: item.jobTitle || item.title || '',
            avatarS3Key: item.avatarS3Key,
            avatarImageUrl:
              getAvatarUrl(item.avatarS3Key) ||
              getAvatarUrl(item.avatarImageUrl) ||
              getAvatarUrl(item.gender === 'FEMALE' ? 'Sarah' : 'James'),
            gender: item.gender || '',
            industry: item.industry || '',
            companySize: item.companySize || '',
            region: item.region || '',
            customerType: item.customerType || '',
            language: item.language || '',
          }));
          setCoaches(fetchedCoaches);
        }

        const sid = editingScenarioId;
        if (!sid) {
          setExistingAttachments([]);
          setScenarioTagsSnapshot([]);
        }
        if (sid) {
          isHydratingScenarioRef.current = true;
          setHasAiGeneratedOnce(true);
          const scenarioRes = await api.getRoleplayScenarioById(sid);
          const raw =
            scenarioRes.data?.data?.data ?? scenarioRes.data?.data ?? scenarioRes.data;
          if (raw && typeof raw === 'object') {
            if (raw.title != null) setTitle(String(raw.title));
            if (raw.description != null) setDescription(String(raw.description));
            if (raw.companyName != null) setCompanyName(String(raw.companyName));
            if (raw.imageS3Key != null && String(raw.imageS3Key).trim() !== '') {
              setImageS3Key(String(raw.imageS3Key));
            } else {
              setImageS3Key(null);
            }
            if (raw.imageUrl != null && String(raw.imageUrl).trim() !== '') {
              setCoverImageUrl(String(raw.imageUrl));
            } else {
              setCoverImageUrl('');
            }
            if (raw.imagePreviewS3Key != null && String(raw.imagePreviewS3Key).trim() !== '') {
              setImagePreviewS3Key(String(raw.imagePreviewS3Key));
            } else {
              setImagePreviewS3Key(null);
            }
            if (raw.imagePreviewUrl != null && String(raw.imagePreviewUrl).trim() !== '') {
              setImagePreviewUrl(String(raw.imagePreviewUrl));
            } else {
              setImagePreviewUrl('');
            }
            if (raw.videoPreviewS3Key != null && String(raw.videoPreviewS3Key).trim() !== '') {
              setVideoPreviewS3Key(String(raw.videoPreviewS3Key));
            } else {
              setVideoPreviewS3Key(null);
            }
            if (raw.videoPreviewUrl != null && String(raw.videoPreviewUrl).trim() !== '') {
              setVideoPreviewUrl(String(raw.videoPreviewUrl));
            } else {
              setVideoPreviewUrl('');
            }
            if (raw.mode) setMode(raw.mode);
            if (raw.rolePlayAgentId != null) setSelectedCoach(raw.rolePlayAgentId);
            if (raw.durationInMinutes != null) setInteractionTime(String(raw.durationInMinutes));
            if (raw.maxAttempts != null) setMaxAttempts(String(raw.maxAttempts));
            if (raw.language) setLanguage(raw.language);
            if (raw.industry) {
              setIndustry(raw.industry);
            } else if (raw.rolePlayAgentId != null) {
              const linkedAgent = agentsList.find((a) => Number(a.id) === Number(raw.rolePlayAgentId));
              if (linkedAgent?.industry) setIndustry(linkedAgent.industry);
            }
            if (raw.companySize) setCompanySize(raw.companySize);
            if (raw.region) setRegion(raw.region);
            if (raw.relationshipType) setRelationshipType(raw.relationshipType);
            if (raw.buyerMood) setBuyerMood(raw.buyerMood);
            if (raw.buyerPressure) setBuyerPressure(raw.buyerPressure);
            if (raw.communicationTone) setCommunicationTone(raw.communicationTone);
            if (raw.replyBehaviour) setReplyBehaviour(raw.replyBehaviour);
            if (raw.buyerStatePrompt != null) setBuyerStatePrompt(String(raw.buyerStatePrompt));
            if (raw.communicationTonePrompt != null) {
              setCommunicationTonePrompt(String(raw.communicationTonePrompt));
            }
            if (raw.replyBehaviourPrompt != null) setReplyBehaviourPrompt(String(raw.replyBehaviourPrompt));
            if (raw.callArcPrompt != null) setCallArcPrompt(String(raw.callArcPrompt));
            if (raw.customAgentRules != null) setCustomAgentRules(String(raw.customAgentRules));
            if (raw.companyContextPrompt != null) {
              const ccp = String(raw.companyContextPrompt);
              setCompanyContextPrompt(ccp);
              if (ccp.trim() !== '') {
                setIsCompanyContextPromptManuallyEdited(true);
              }
            }
            if (raw.interruptLogicPrompt != null) {
              setInterruptLogicPrompt(String(raw.interruptLogicPrompt));
            }
            if (raw.coachFlowPrompt != null) setCoachFlowPrompt(String(raw.coachFlowPrompt));
            if (raw.openingMessage != null) setOpeningMessage(String(raw.openingMessage));
            if (Array.isArray(raw.openingPromptSuggestions)) {
              setConversationPrompts(raw.openingPromptSuggestions);
            }
            if (Array.isArray(raw.objections)) {
              setObjections(raw.objections);
            } else {
              setObjections([]);
            }
            scenarioSkillWeightagesNullRef.current = raw.skillWeightages == null;
            didInitSkillWeightsRef.current = false;
            if (typeof raw.skillWeightages === 'object' && raw.skillWeightages !== null) {
              setSkillWeightages(raw.skillWeightages);
            } else if (Array.isArray(raw.rolePlayTags) && raw.rolePlayTags.length > 0) {
              // Edit API may return weightage on each rolePlayTag instead of top-level skillWeightages.
              const bySkill = {};
              raw.rolePlayTags.forEach((tag) => {
                const skillName =
                  tag?.skillName != null && String(tag.skillName).trim() !== ''
                    ? String(tag.skillName).trim()
                    : null;
                if (!skillName) return;
                if (bySkill[skillName] != null) return;
                const numericWeight = Number(tag?.weightage);
                if (Number.isNaN(numericWeight)) return;
                bySkill[skillName] = String(numericWeight);
              });
              setSkillWeightages(bySkill);
            } else {
              // Scenario GET may return `skillWeightages: null`; initialize later once tags are selected.
              setSkillWeightages({});
            }
            setRolePlayType(raw.privateScenario === false ? 'Public' : 'Partner only');
            setRecommended(raw.recommended === true ? true : raw.recommended === false ? false : null);
            setArchived(raw.archived === true);
            setPublished(raw.published !== false);
            setDifficulty(normalizeDifficulty(raw.difficulty));
            if (Array.isArray(raw.rolePlayLearners) && raw.rolePlayLearners.length) {
              const labels = raw.rolePlayLearners
                .map((l) => getPersonaDisplayKey(l))
                .filter(Boolean);
              setSelectedPersonas(labels);
            } else if (Array.isArray(raw.learnerRoleIds) && raw.learnerRoleIds.length && personasList.length) {
              const labels = raw.learnerRoleIds
                .map((rid) => getPersonaDisplayKey(personasList.find(
                  (p) => p.id === rid || Number(p.id) === Number(rid)
                )))
                .filter(Boolean);
              setSelectedPersonas(labels);
            }
            if (Array.isArray(raw.rolePlayTags) && raw.rolePlayTags.length) {
              setScenarioTagsSnapshot(raw.rolePlayTags);
              const ids = raw.rolePlayTags
                .map((t) => t.id)
                .filter((id) => id != null)
                .map((id) => Number(id))
                .filter((id) => Number.isFinite(id));
              setSelectedTagIds(ids);
              const skillNames = [
                ...new Set(
                  raw.rolePlayTags
                    .map((t) =>
                      t?.skillName != null && String(t.skillName).trim() !== ''
                        ? String(t.skillName).trim()
                        : null
                    )
                    .filter(Boolean)
                ),
              ];
              if (skillNames.length > 0) {
                setSelectedSkillNames(skillNames);
              }
            } else if (Array.isArray(raw.tagIds) && raw.tagIds.length) {
              setScenarioTagsSnapshot([]);
              setSelectedTagIds(raw.tagIds.map((id) => Number(id)).filter((id) => Number.isFinite(id)));
            } else {
              setScenarioTagsSnapshot([]);
            }
            if (Array.isArray(raw.rolePlayAttachments)) {
              const list = raw.rolePlayAttachments
                .filter((a) => a?.s3Key != null && String(a.s3Key).trim() !== '')
                .map((a) => ({
                  id: a.id,
                  title: a.title != null ? String(a.title) : 'Attachment',
                  contentType: a.contentType != null ? String(a.contentType) : 'application/octet-stream',
                  s3Key: String(a.s3Key).trim(),
                  configJson: a.configJson,
                }));
              setExistingAttachments(list);
            } else {
              setExistingAttachments([]);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching AI Coach setup data:", error);
        message.error("Error fetching data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [editingScenarioId, selectedVendorId]);

  const clearSkillSelections = useCallback(() => {
    setSelectedSkillNames([]);
    setSelectedTagIds([]);
    setSkillWeightages({});
    didInitSkillWeightsRef.current = false;
  }, []);

  const prevVendorIdRef = useRef(selectedVendorId);
  useEffect(() => {
    if (prevVendorIdRef.current === selectedVendorId) return;
    prevVendorIdRef.current = selectedVendorId;
    if (isHydratingScenarioRef.current) return;
    setSelectedCoach(null);
    setSelectedPersonas([]);
    clearSkillSelections();
    setIndustry('');
  }, [selectedVendorId, clearSkillSelections]);

  const applyCoachToScenario = useCallback((coach) => {
    if (!coach) return;
    setSelectedCoach(coach.id);
    if (coach.industry && industry && industry !== coach.industry) {
      clearSkillSelections();
    }
    if (coach.industry) setIndustry(coach.industry);
    if (coach.companySize) setCompanySize(coach.companySize);
    if (coach.region) setRegion(coach.region);
    if (coach.customerType) setRelationshipType(coach.customerType);
    if (coach.language) setLanguage(coach.language);
  }, [industry, clearSkillSelections]);

  const handleIndustryChange = (e) => {
    const next = e.target.value;
    if (next !== industry) {
      clearSkillSelections();
    }
    setIndustry(next);
  };

  const didCheckUrlAgentRef = useRef(false);
  useEffect(() => {
    if (didCheckUrlAgentRef.current) return;
    if (coaches.length === 0) return;
    if (editingScenarioId) return;
    didCheckUrlAgentRef.current = true;
    const idFromUrl = Number.parseInt(searchParams.get('rolePlayAgentId') || '', 10);
    const matched =
      Number.isFinite(idFromUrl) && coaches.find((c) => Number(c.id) === idFromUrl);
    if (matched) {
      applyCoachToScenario(matched);
    }
  }, [editingScenarioId, searchParams, coaches, applyCoachToScenario]);

  useEffect(() => {
    if (isCompanyContextPromptManuallyEdited) return;
    const hasCompanySectionInput =
      (companyName != null && String(companyName).trim() !== '') ||
      (industry != null && String(industry).trim() !== '') ||
      (companySize != null && String(companySize).trim() !== '') ||
      (region != null && String(region).trim() !== '') ||
      (relationshipType != null && String(relationshipType).trim() !== '') ||
      (language != null && String(language).trim() !== '');
    if (!hasCompanySectionInput) {
      setCompanyContextPrompt('');
      return;
    }
    setCompanyContextPrompt(
      buildGenericCompanyContextPrompt({
        companyName,
        industry,
        companySize,
        region,
        relationshipType,
        language,
      })
    );
  }, [
    companyName,
    industry,
    companySize,
    region,
    relationshipType,
    language,
    isCompanyContextPromptManuallyEdited,
  ]);

  /** Unique `skillName` values from tags API; tags per skill as `{ id, label }` (IDs are unique even when labels repeat across skills). */
  const { skillNamesFromTags, tagsBySkillName } = useMemo(() => {
    const map = new Map();
    mergedTags.forEach((tag) => {
      const sk = tag.skillName != null && String(tag.skillName).trim() !== '' ? String(tag.skillName).trim() : null;
      if (!sk) return;
      const lbl = tag.label || tag.name;
      if (!lbl) return;
      if (tag.id == null) return;
      const nid = Number(tag.id);
      if (!Number.isFinite(nid)) return;
      if (!map.has(sk)) map.set(sk, []);
      const arr = map.get(sk);
      if (!arr.some((x) => Number(x.id) === nid)) {
        arr.push({ id: nid, label: lbl });
      }
    });
    const keys = [...map.keys()].sort((a, b) => a.localeCompare(b));
    keys.forEach((k) => {
      map.get(k).sort((a, b) => a.label.localeCompare(b.label));
    });
    return { skillNamesFromTags: keys, tagsBySkillName: map };
  }, [mergedTags]);

  const selectedTagPreviewItems = useMemo(() => {
    return selectedTagIds.map((id) => {
      const t = findTagById(id);
      return { id, label: t?.label || t?.name || String(id) };
    });
  }, [selectedTagIds, findTagById]);

  useEffect(() => {
    const tagSkills = selectedTagIds
      .map((id) => findTagById(id))
      .filter(Boolean)
      .map((tag) =>
        tag.skillName != null && String(tag.skillName).trim() !== ''
          ? String(tag.skillName).trim()
          : null
      )
      .filter(Boolean);
    const weightSkills = Object.keys(skillWeightages).filter((sk) => {
      const v = skillWeightages[sk];
      return v !== '' && v != null && skillNamesFromTags.includes(sk);
    });
    const merged = [...new Set([...selectedSkillNames, ...tagSkills, ...weightSkills])].filter((sk) =>
      skillNamesFromTags.includes(sk)
    );
    if (merged.length !== selectedSkillNames.length || merged.some((v, i) => v !== selectedSkillNames[i])) {
      setSelectedSkillNames(merged);
    }
  }, [selectedTagIds, findTagById, skillWeightages, skillNamesFromTags, selectedSkillNames]);

  useEffect(() => {
    setSkillWeightages((current) => {
      let changed = false;
      const next = { ...current };
      skillNamesFromTags.forEach((key) => {
        if (next[key] === undefined) {
          next[key] = '';
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [skillNamesFromTags]);


  useEffect(() => {
    if (selectedSkillNames.length === 0) return;
    setSkillWeightages((current) => {
      const hasMissing = selectedSkillNames.some((sk) => {
        const raw = current[sk];
        return raw === '' || raw == null || Number.isNaN(Number(raw));
      });
      if (!hasMissing) return current;

      const n = selectedSkillNames.length;
      const base = Math.floor(100 / n);
      const remainder = 100 - base * n;
      const weights = { ...current };
      selectedSkillNames.forEach((sk, idx) => {
        weights[sk] = String(base + (idx < remainder ? 1 : 0));
      });
      return weights;
    });
  }, [selectedSkillNames]);

  // Ensure every selected skill has at least one tag selected
  useEffect(() => {
    if (isHydratingScenarioRef.current || tagsLoading) return;
    if (selectedSkillNames.length === 0) return;
    const missing = selectedSkillNames.filter((skillName) => {
      return !selectedTagIds.some((id) => {
        const tag = findTagById(id);
        const sk = tag?.skillName != null && String(tag.skillName).trim() !== ''
          ? String(tag.skillName).trim() : null;
        return sk === skillName;
      });
    });
    if (missing.length === 0) return;
    const addIds = [];
    missing.forEach((sk) => {
      const tags = tagsBySkillName.get(sk) || [];
      tags.forEach((t) => addIds.push(t.id));
    });
    if (addIds.length > 0) {
      setSelectedTagIds((prev) => [...new Set([...prev, ...addIds])]);
    }
  }, [selectedSkillNames, selectedTagIds, tagsBySkillName, findTagById]);

  // If we loaded a scenario where the backend returned `skillWeightages: null`,
  // pre-fill weights so the "total must be 100" rule doesn't permanently block editing.
  useEffect(() => {
    if (!editingScenarioId) return;
    if (!scenarioSkillWeightagesNullRef.current) return;
    if (didInitSkillWeightsRef.current) return;
    if (skillNamesFromTags.length === 0) return;

    const n = skillNamesFromTags.length;
    const base = Math.floor(100 / n);
    const remainder = 100 - base * n;

    setSkillWeightages(() => {
      const next = {};
      skillNamesFromTags.forEach((key, idx) => {
        const value = base + (idx < remainder ? 1 : 0);
        next[key] = String(value);
      });
      return next;
    });

    didInitSkillWeightsRef.current = true;
  }, [editingScenarioId, skillNamesFromTags]);

  const totalWeightage = selectedSkillNames.reduce((sum, key) => {
    const numeric = Number(skillWeightages[key] || 0);
    return sum + (Number.isNaN(numeric) ? 0 : numeric);
  }, 0);

  const skillWeightErrors = useMemo(() => {
    const errors = {};
    selectedSkillNames.forEach((skillName) => {
      const raw = skillWeightages[skillName];
      const numeric = Number(raw);
      if (raw === '' || raw == null || Number.isNaN(numeric)) {
        errors[skillName] = 'Enter a valid number';
        return;
      }
      if (numeric <= 0) {
        errors[skillName] = 'Weight must be greater than 0';
      }
    });
    return errors;
  }, [selectedSkillNames, skillWeightages]);

  const skillsMissingTagSelection = useMemo(() => {
    return selectedSkillNames.filter((skillName) => {
      return !selectedTagIds.some((id) => {
        const tag = findTagById(id);
        const sk =
          tag?.skillName != null && String(tag.skillName).trim() !== ''
            ? String(tag.skillName).trim()
            : null;
        return sk === skillName;
      });
    });
  }, [selectedSkillNames, selectedTagIds, findTagById]);

  const toggleSkillSelection = (skillName) => {
    const isOn = selectedSkillNames.includes(skillName);
    if (isOn) {
      const next = selectedSkillNames.filter((s) => s !== skillName);
      setSelectedTagIds((prev) =>
        prev.filter((id) => {
          const tag = findTagById(id);
          const sk =
            tag?.skillName != null && String(tag.skillName).trim() !== ''
              ? String(tag.skillName).trim()
              : null;
          return sk !== skillName;
        })
      );
      setSelectedSkillNames(next);
      redistributeWeightages(next);
      return;
    }
    const next = [...selectedSkillNames, skillName];
    const tagIdsForSkill = (tagsBySkillName.get(skillName) || []).map(t => t.id);
    setSelectedTagIds((prev) => [...prev, ...tagIdsForSkill]);
    setSelectedSkillNames(next);
    redistributeWeightages(next);
  };

  const redistributeWeightages = (names) => {
    if (names.length === 0) return;
    const n = names.length;
    const base = Math.floor(100 / n);
    const remainder = 100 - base * n;
    const weights = {};
    names.forEach((sk, idx) => {
      weights[sk] = String(base + (idx < remainder ? 1 : 0));
    });
    setSkillWeightages(weights);
  };

  const selectedCoachName = useMemo(() => {
    const c = coaches.find((x) => Number(x.id) === Number(selectedCoach));
    return c?.name ?? '';
  }, [coaches, selectedCoach]);

  const handleToggle = (item, currentList, setter, max = 15) => {
    if (currentList.includes(item)) {
      setter(currentList.filter(i => i !== item));
    } else if (currentList.length < max) {
      setter([...currentList, item]);
    }
  };

  const handleTagToggle = (tagId, skillName) => {
    const id = Number(tagId);
    if (!Number.isFinite(id)) return;
    const tag = findTagById(id);
    if (!tag) return;
    const sk =
      tag.skillName != null && String(tag.skillName).trim() !== ''
        ? String(tag.skillName).trim()
        : null;
    if (sk !== skillName) return;

    if (selectedTagIds.includes(id)) {
      setSelectedTagIds((prev) => prev.filter((x) => x !== id));
      return;
    }
    setSelectedTagIds((prev) => [...prev, id]);
  };

  const handleAddPrompt = () => {
    if (!promptInput.trim()) return;
    setConversationPrompts([...conversationPrompts, promptInput]);
    setPromptInput('');
  };

  const proceedToScenario = async (promptText) => {
    setAiLoading(true);
    try {
      const quality = evaluatePromptQuality(promptText);
      const impliedDifficulty = quality.category === 'needs_enrichment' ? 'EASY' : quality.category === 'needs_refinement' ? 'MEDIUM' : 'HARD';
      const personaLabels = availablePersonas.map((p) => getPersonaDisplayKey(p)).filter(Boolean);
      const effectivePrompt = hasAiGeneratedOnce
        ? `${promptText}\n\n[Regeneration request #${Date.now()}: Generate a completely fresh, unique roleplay scenario variation with different title, description, buyer background, objections, and opening message.]`
        : promptText;

      const genRes = await api.generateScenarioFromPrompt(
        effectivePrompt,
        null,
        personaLabels,
        skillNamesFromTags,
        language,
        null,
        null,
        impliedDifficulty
      );
      await applyScenarioData(genRes.data.data, promptText);
      setHasAiGeneratedOnce(true);
      message.success(hasAiGeneratedOnce ? 'Scenario regenerated with fresh content' : 'Scenario generated from prompt');
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to generate scenario from prompt');
    } finally {
      setAiLoading(false);
    }
  };

  const evaluatePromptQuality = (prompt) => {
    const hasMood = MOOD_DETECT_PATTERNS.some(({ patterns }) => patterns.some(p => p.test(prompt)));
    const hasPressure = PRESSURE_DETECT_PATTERNS.some(({ patterns }) => patterns.some(p => p.test(prompt)));
    const wordCount = prompt.trim().split(/\s+/).length;

    if (hasMood && hasPressure && wordCount >= 12) {
      return { category: 'ready', count: 2, purpose: 'Improve clarity. Improve realism. Preserve intent.' };
    } else if (hasMood || hasPressure) {
      return { category: 'needs_refinement', count: 3, purpose: 'Strengthen weaker semantic signals. Improve wording.' };
    } else {
      return { category: 'needs_enrichment', count: 5, purpose: 'Introduce missing semantic signals.' };
    }
  };

  const handleDifficultyChange = (value) => {
    setDifficulty(value);
    if (title) {
      const promptText = aiPrompt.trim();
      if (promptText) {
        regenerateScenarioWithDifficulty(promptText, value);
      }
    }
  };

  const regenerateScenarioWithDifficulty = async (promptText, difficultyLevel) => {
    setAiLoading(true);
    try {
      const personaLabels = availablePersonas.map((p) => getPersonaDisplayKey(p)).filter(Boolean);
      const effectivePrompt = `${promptText}\n\n[Regeneration request #${Date.now()}: Generate a completely fresh, unique roleplay scenario variation tailored to ${difficultyLevel} difficulty with different title, description, buyer background, objections, and opening message.]`;
      const genRes = await api.generateScenarioFromPrompt(
        effectivePrompt,
        null,
        personaLabels,
        skillNamesFromTags,
        language,
        null,
        null,
        difficultyLevel
      );
      await applyScenarioData(genRes.data.data, promptText, difficultyLevel);
      setHasAiGeneratedOnce(true);
      message.success('Scenario regenerated with new difficulty');
    } catch (err) {
      message.error(err?.response?.data?.message || 'Failed to regenerate scenario');
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;

    if (hasAiGeneratedOnce) {
      setShowRefinementPicker(false);
      setRefinementsData(null);
      await proceedToScenario(aiPrompt);
      return;
    }

    const offTopicPatterns = [
      /^(who|what|when|where|why)\s+(is|was|are|were)\s/i,
      /^how\s+(is|was|are)\s/i,
      /^(define|explain|describe|list|name)\s+(the|me|us|a|an)\s/i,
      /^(explain|define|describe)\s+about\s/i,
      /^(tell me|tell us)\s/i,
      /(capital\s+of|president\s+of|prime\s+minister|chief\s+minister|population\s+of|weather\s+in|history\s+of)\s/i,
    ];
    const lowerPrompt = aiPrompt.trim().toLowerCase();

    const isOffTopic = offTopicPatterns.some((pat) => pat.test(lowerPrompt));
    if (isOffTopic) {
      setShowVagueSuggestion(true);
      setRefinementsData(null);
      setShowRefinementPicker(false);
      return;
    }

    // Evaluate quality locally (no API call needed)
    const quality = evaluatePromptQuality(aiPrompt);
    const impliedDifficulty = quality.category === 'needs_enrichment' ? 'EASY' : quality.category === 'needs_refinement' ? 'MEDIUM' : 'HARD';
    setDifficulty(impliedDifficulty);

    // All categories go through the refinement flow (Ready shows 2 polish suggestions)
    setShowRefinementPicker(false);
    setRefinementsLoading(true);
    try {
      const personaLabels = availablePersonas.map((p) => getPersonaDisplayKey(p)).filter(Boolean);
      const refRes = await api.suggestPromptRefinements(aiPrompt, personaLabels, skillNamesFromTags, language);
      const refData = refRes.data.data;
      if (refData?.refinements?.length > 0) {
        setRefinementsData(refData);
        setShowRefinementPicker(true);
      } else {
        proceedToScenario(aiPrompt);
      }
    } catch (err) {
      message.error('Prompt refinement unavailable — generating scenario directly');
      proceedToScenario(aiPrompt);
    } finally {
      setRefinementsLoading(false);
    }
  };

  const handleVagueSuggestionSelect = (suggestion) => {
    setAiPrompt(suggestion);
    setShowVagueSuggestion(false);
    proceedToScenario(suggestion);
  };

  const handleRefinementSelect = async (refinedText) => {
    setShowRefinementPicker(false);
    setRefinementsData(null);
    setAiPrompt(refinedText);
    await proceedToScenario(refinedText);
  };

  const applyScenarioData = async (data, promptText, forcedDifficulty) => {
    setHasAiGeneratedOnce(true);
    const quality = forcedDifficulty ? null : evaluatePromptQuality(promptText || aiPrompt);
    const impliedDifficulty = forcedDifficulty || (quality.category === 'needs_enrichment' ? 'EASY' : quality.category === 'needs_refinement' ? 'MEDIUM' : 'HARD');
    setDifficulty(impliedDifficulty);
    setTitle(data.title || '');
    setDescription(data.description || '');

    const aiPersonas = data.learnerPersonas || [];
    let matched = aiPersonas
      .map((label) => {
        const needle = String(label || '').trim().toLowerCase();
        if (!needle) return null;
        let found = availablePersonas.find((p) => {
          const pLabel = String(p.label || '').trim().toLowerCase();
          const pName = String(p.name || '').trim().toLowerCase();
          const pKey = getPersonaDisplayKey(p).toLowerCase();
          return pKey === needle || pLabel === needle || pName === needle;
        });
        if (!found) {
          const needleSingular = needle.replace(/s$/, '');
          found = availablePersonas.find((p) => {
            const pKey = getPersonaDisplayKey(p).toLowerCase().replace(/s$/, '');
            return pKey === needleSingular;
          });
        }
        if (!found) {
          found = availablePersonas.find((p) => {
            const pKey = getPersonaDisplayKey(p).toLowerCase();
            return pKey.includes(needle) || needle.includes(pKey);
          });
        }
        return found;
      })
      .filter(Boolean)
      .map((p) => getPersonaDisplayKey(p));

    matched = [...new Set(matched)];

    if (matched.length === 0 && availablePersonas.length > 0) {
      const firstKey = getPersonaDisplayKey(availablePersonas[0]);
      if (firstKey) {
        matched = [firstKey];
      }
    }

    setSelectedPersonas(matched);
    setPersonaMatchFailed(matched.length === 0);

    if (data.agentId != null) {
      const targetId = Number(data.agentId);
      const matchedCoach = coaches.find((c) => Number(c.id) === targetId);
      if (matchedCoach) {
        applyCoachToScenario(matchedCoach);
      } else {
        setSelectedCoach(targetId);
      }
    }

    let mood = data.buyerMood || '';
    let pressure = data.buyerPressure || '';
    if (mood && !pressure) {
      pressure = detectPressureFromText(promptText || '') || '';
    }
    // Last-resort: derive pressure from scenario description
    if (!pressure) {
      pressure = detectPressureFromText(data.description || '');
    }
    // Final fallback: pick defaults based on difficulty
    if (!mood) {
      mood = 'SKEPTICAL';
    }
    if (!pressure) {
      const defaultPressure = { EASY: 'COMPETITIVE_EVALUATION', MEDIUM: 'BUDGET_PRESSURE', HARD: 'ROI_SCRUTINY' };
      pressure = defaultPressure[impliedDifficulty] || 'BUDGET_PRESSURE';
    }
    // Ensure mood/pressure are valid for the current difficulty tier
    const allowedMoods = DIFFICULTY_MOODS[impliedDifficulty];
    const allowedPressures = DIFFICULTY_PRESSURES[impliedDifficulty];
    if (allowedMoods && !allowedMoods.has(mood)) {
      mood = 'SKEPTICAL';
    }
    if (allowedPressures && !allowedPressures.has(pressure)) {
      const defaultPressure = { EASY: 'COMPETITIVE_EVALUATION', MEDIUM: 'BUDGET_PRESSURE', HARD: 'ROI_SCRUTINY' };
      pressure = defaultPressure[impliedDifficulty] || 'BUDGET_PRESSURE';
    }
    setBuyerMood(mood);
    setBuyerPressure(pressure);
    if (data.buyerStatePrompt != null && String(data.buyerStatePrompt).trim()) {
      setBuyerStatePrompt(String(data.buyerStatePrompt));
    } else {
      setBuyerStatePrompt(composeBuyerStatePrompt(mood, pressure));
    }

    // Use AI-generated tone/reply when available, fall back to hardcoded mapping
    const aiTone = data.communicationTone;
    const aiReply = data.replyBehaviour;
    if (aiTone) {
      setCommunicationTone(aiTone);
    } else {
      const mapping = MOOD_PRESSURE_TO_BEHAVIOR[mood]?.[pressure] || MOOD_DEFAULT_BEHAVIOR[mood];
      if (mapping) {
        setCommunicationTone(mapping.tone);
      } else if (mood && pressure) {
        setCommunicationTone('');
      }
    }
    if (aiReply) {
      setReplyBehaviour(aiReply);
    } else {
      const mapping = MOOD_PRESSURE_TO_BEHAVIOR[mood]?.[pressure] || MOOD_DEFAULT_BEHAVIOR[mood];
      if (mapping) {
        setReplyBehaviour(mapping.reply);
      } else if (mood && pressure) {
        setReplyBehaviour('');
      }
    }
    const aiTonePrompt = data.communicationTonePrompt;
    const aiReplyPrompt = data.replyBehaviourPrompt;
    const mapping = MOOD_PRESSURE_TO_BEHAVIOR[mood]?.[pressure] || MOOD_DEFAULT_BEHAVIOR[mood];
    setCommunicationTonePrompt(aiTonePrompt && String(aiTonePrompt).trim() ? String(aiTonePrompt) : (mapping?.tone ? COMMUNICATION_TONE_PROMPTS[mapping.tone] || '' : ''));
    setReplyBehaviourPrompt(aiReplyPrompt && String(aiReplyPrompt).trim() ? String(aiReplyPrompt) : (mapping?.reply ? REPLY_BEHAVIOUR_PROMPTS[mapping.reply] || '' : ''));

    setOpeningMessage(data.openingMessage || '');
    setConversationPrompts(data.conversationPrompts || []);
    if (data.callArcPrompt != null) setCallArcPrompt(String(data.callArcPrompt));
    if (Array.isArray(data.objections)) setObjections(data.objections);

    // Apply AI industry, then load industry-scoped tags before matching skills/tags
    let tagsForMatching = mergedTags;
    const aiIndustry = data.industry != null && String(data.industry).trim() !== ''
      ? String(data.industry).trim().toUpperCase().replace(/[\s-]+/g, '_')
      : null;
    if (aiIndustry) {
      if (aiIndustry !== industry) {
        clearSkillSelections();
      }
      setIndustry(aiIndustry);
      tagsForMatching = await fetchTagsForIndustry(aiIndustry);
    }

    const skillKeys = new Set();
    const tagsBySkill = new Map();
    (tagsForMatching || []).forEach((tag) => {
      const sk = tag.skillName != null && String(tag.skillName).trim() !== '' ? String(tag.skillName).trim() : null;
      if (!sk) return;
      skillKeys.add(sk);
      if (!tagsBySkill.has(sk)) tagsBySkill.set(sk, []);
      const arr = tagsBySkill.get(sk);
      if (tag.id != null && !arr.some((x) => Number(x.id) === Number(tag.id))) {
        arr.push({ id: tag.id, label: tag.label || tag.name });
      }
    });

    const requestedNames = new Set([
      ...(data.tagNames || []),
      ...(data.skillNames || [])
    ].map((n) => String(n).toLowerCase().trim()));

    const matchedTagItems = (tagsForMatching || []).filter((t) => {
      const tName = String(t.name || '').toLowerCase().trim();
      const tLabel = String(t.label || '').toLowerCase().trim();
      return requestedNames.has(tName) || requestedNames.has(tLabel);
    });

    const matchedSkillNamesSet = new Set();
    matchedTagItems.forEach((t) => {
      if (t.skillName && skillKeys.has(String(t.skillName).trim())) {
        matchedSkillNamesSet.add(String(t.skillName).trim());
      }
    });
    (data.skillNames || []).forEach((sk) => {
      if (skillKeys.has(sk)) matchedSkillNamesSet.add(sk);
    });

    const finalSkillNames = Array.from(matchedSkillNamesSet);
    let finalTagIds = matchedTagItems.map((t) => t.id).filter((id) => id != null);

    if (finalTagIds.length === 0 && finalSkillNames.length > 0) {
      finalSkillNames.forEach((sk) => {
        const tags = tagsBySkill.get(sk) || [];
        tags.forEach((t) => finalTagIds.push(t.id));
      });
    }

    if (finalSkillNames.length > 0 || finalTagIds.length > 0) {
      setSelectedTagIds(finalTagIds.map(Number).filter(Number.isFinite));
      setSelectedSkillNames(finalSkillNames);
      redistributeWeightages(finalSkillNames);
    }
  };

  const handleRemovePrompt = (index) => {
    setConversationPrompts(conversationPrompts.filter((_, i) => i !== index));
  };

  const handleAddObjection = () => {
    if (!objectionInput.trim()) return;
    setObjections([...objections, objectionInput.trim()]);
    setObjectionInput('');
  };

  const handleRemoveObjection = (index) => {
    setObjections(objections.filter((_, i) => i !== index));
  };

  const handleRegenerateObjections = async () => {
    if (!aiPrompt.trim() && !title.trim() && !description.trim()) return;
    setObjectionRegenLoading(true);
    try {
      const res = await api.generateObjections({
        prompt: aiPrompt.trim() || title.trim() || description.trim(),
        title: title.trim() || null,
        description: description.trim() || null,
      });
      const data = res.data?.data;
      if (Array.isArray(data?.objections)) {
        setObjections(data.objections);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to regenerate objections';
      message.error(msg);
    } finally {
      setObjectionRegenLoading(false);
    }
  };

  const handleRegenerateBehaviorPrompts = async (opts = {}) => {
    const regenerateOnly = opts.regenerateOnly || null;
    setBehaviorPromptsLoading(true);
    try {
      const res = await api.generateBehaviorPrompts({
        prompt: aiPrompt.trim() || title.trim() || description.trim(),
        title: title.trim() || null,
        description: description.trim() || null,
        buyerMood: opts.buyerMood !== undefined ? opts.buyerMood : buyerMood,
        buyerPressure: opts.buyerPressure !== undefined ? opts.buyerPressure : buyerPressure,
        communicationTone: opts.communicationTone !== undefined ? opts.communicationTone : communicationTone,
        replyBehaviour: opts.replyBehaviour !== undefined ? opts.replyBehaviour : replyBehaviour,
      });
      const data = res.data?.data;
      if (!regenerateOnly || regenerateOnly === 'buyerStatePrompt') {
        if (data?.buyerStatePrompt != null) {
          setBuyerStatePrompt(String(data.buyerStatePrompt));
        }
      }
      if (!regenerateOnly || regenerateOnly === 'communicationTonePrompt') {
        if (data?.communicationTonePrompt != null) {
          setCommunicationTonePrompt(String(data.communicationTonePrompt));
        }
      }
      if (!regenerateOnly || regenerateOnly === 'replyBehaviourPrompt') {
        if (data?.replyBehaviourPrompt != null) {
          setReplyBehaviourPrompt(String(data.replyBehaviourPrompt));
        }
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to regenerate behavior prompts';
      message.error(msg);
    } finally {
      setBehaviorPromptsLoading(false);
    }
  };

  const handleRegenerateConversationPrompts = async () => {
    if (!aiPrompt.trim() && !title.trim() && !description.trim()) return;
    setConvPromptRegenLoading(true);
    try {
      const res = await api.generateConversationPrompts({
        prompt: aiPrompt.trim() || title.trim() || description.trim(),
        title: title.trim() || null,
        description: description.trim() || null,
        openingMessage: openingMessage.trim() || null,
      });
      const data = res.data?.data;
      if (Array.isArray(data?.conversationPrompts)) {
        setConversationPrompts(data.conversationPrompts);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err.message || 'Failed to regenerate starter prompts';
      message.error(msg);
    } finally {
      setConvPromptRegenLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validExtensions = [
      'application/pdf',
      'video/mp4',
      'video/quicktime',
      'video/webm',
      'image/png',
      'image/jpeg',
      'image/jpg',
      'image/webp',
      'image/gif'
    ];

    const validFiles = selectedFiles.filter(file => {
      const isValid = validExtensions.includes(file.type) || file.type.startsWith('image/');
      if (!isValid) {
        message.error(`${file.name} is not a valid file type (PDF, MP4, MOV, PNG, JPG, WebP, GIF).`);
      }
      return isValid;
    });

    if (existingAttachments.length + files.length + validFiles.length > 3) {
      message.error("You can only upload a maximum of 3 files.");
      return;
    }

    setFiles([...files, ...validFiles]);
    // Clear input value to allow re-selecting same file if needed
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const handleCoverImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.error('Please choose an image file (PNG, JPG, WebP, or GIF).');
      if (coverImageInputRef.current) coverImageInputRef.current.value = '';
      return;
    }

    setCoverImageUploading(true);
    try {
      const urlRes = await api.getRoleplayScenarioImageUploadUrl({
        fileName: file.name,
        contentType: file.type,
        ...(selectedVendorId != null ? { vendorId: selectedVendorId } : {}),
      });

      if (!urlRes.data?.success || !urlRes.data?.data) {
        throw new Error(urlRes.data?.message || 'Failed to get image upload URL');
      }

      const {
        url: uploadUrl,
        signedHeaders,
        imageS3Key: keyFromApi,
        s3Key,
        imageUrl,
        publicUrl,
      } = urlRes.data.data;
      const resolvedKey = keyFromApi ?? s3Key;
      if (!uploadUrl || !resolvedKey) {
        throw new Error('Invalid upload URL response');
      }

      await axios.put(uploadUrl, file, {
        headers: {
          ...signedHeaders,
          'Content-Type': file.type,
        },
      });

      setImageS3Key(resolvedKey);
      const readableUrl = imageUrl || publicUrl || '';
      if (readableUrl) {
        setCoverImageUrl(readableUrl);
      } else {
        const localPreviewUrl = URL.createObjectURL(file);
        setCoverImageUrl(localPreviewUrl);
      }
      message.success('Cover image ready');
    } catch (err) {
      console.error('Cover image upload failed:', err);
      message.error(
        err?.response?.data?.message || err?.message || 'Failed to upload cover image'
      );
    } finally {
      setCoverImageUploading(false);
      if (coverImageInputRef.current) coverImageInputRef.current.value = '';
    }
  };

  const clearCoverImage = () => {
    setImageS3Key(null);
    setCoverImageUrl('');
  };

  const uploadPreviewMedia = async (file, mediaType) => {
    const isImage = mediaType === 'image';
    const setUploading = isImage ? setImagePreviewUploading : setVideoPreviewUploading;
    const inputRef = isImage ? imagePreviewInputRef : videoPreviewInputRef;

    setUploading(true);
    try {
      const urlRes = await api.getRoleplayScenarioMediaUploadUrl({
        fileName: file.name,
        contentType: file.type,
        ...(selectedVendorId != null ? { vendorId: selectedVendorId } : {}),
      });

      if (!urlRes.data?.success || !urlRes.data?.data) {
        throw new Error(urlRes.data?.message || 'Failed to get media upload URL');
      }

      const { url: uploadUrl, s3Key, signedHeaders } = urlRes.data.data;
      if (!uploadUrl || !s3Key) {
        throw new Error('Invalid media upload URL response');
      }

      await axios.put(uploadUrl, file, {
        headers: {
          ...(signedHeaders || {}),
          'Content-Type': file.type,
        },
      });

      if (isImage) {
        setImagePreviewS3Key(s3Key);
        setImagePreviewUrl(URL.createObjectURL(file));
      } else {
        setVideoPreviewS3Key(s3Key);
        setVideoPreviewUrl(URL.createObjectURL(file));
      }
      message.success(`${isImage ? 'Image' : 'Video'} preview ready`);
    } catch (err) {
      console.error(`${isImage ? 'Image' : 'Video'} preview upload failed:`, err);
      message.error(
        err?.response?.data?.message || err?.message || `Failed to upload ${isImage ? 'image' : 'video'} preview`
      );
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleImagePreviewSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      message.error('Please choose an image file (PNG, JPG, WebP, or GIF).');
      if (imagePreviewInputRef.current) imagePreviewInputRef.current.value = '';
      return;
    }
    await uploadPreviewMedia(file, 'image');
  };

  const handleVideoPreviewSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/webm'];
    if (!validVideoTypes.includes(file.type)) {
      message.error('Please choose a video file (MP4, MOV, or WebM).');
      if (videoPreviewInputRef.current) videoPreviewInputRef.current.value = '';
      return;
    }
    await uploadPreviewMedia(file, 'video');
  };

  const clearImagePreview = () => {
    setImagePreviewS3Key(null);
    setImagePreviewUrl('');
  };

  const clearVideoPreview = () => {
    setVideoPreviewS3Key(null);
    setVideoPreviewUrl('');
  };

  const validate = () => {
    if (!title?.trim()) return "Scenario Title is required";
    if (!description?.trim()) return "Scenario Description is required";
    if (selectedPersonas.length === 0) return "Please select at least one Target Learner Persona";
    const resolvedLearnerIds = selectedPersonas
      .map((key) => resolvePersonaId(key, availablePersonas))
      .filter((id) => id != null);
    if (resolvedLearnerIds.length === 0) {
      return "Could not resolve learner persona IDs. Try re-selecting target learner personas.";
    }
    if (selectedTagIds.length === 0) return "Please select at least one skill tag";
    if (!selectedCoach) return "Please select a buyer persona";
    if (!industry) return "Please select an industry";
    if (!callArcPrompt?.trim()) return "Call Flow Prompt is required";
    if (!rolePlayType) return "Please select Role-Play Type";
    if (!difficulty) return "Please select Difficulty";
    if (recommended !== true && recommended !== false) return "Please select Recommended";
    const duration = Number.parseInt(interactionTime, 10);
    if (!interactionTime || Number.isNaN(duration) || duration < 1) {
      return "Expected Interaction Time is required";
    }
    const maxA = Number(maxAttempts);
    if (maxAttempts === '' || maxAttempts == null || Number.isNaN(maxA) || maxA < 1) {
      return "Max Attempts must be at least 1";
    }
    if (selectedSkillNames.length === 0) return "Please select at least one skill";
    if (Object.keys(skillWeightErrors).length > 0) {
      return "Each selected skill must have weightage greater than 0";
    }
    if (skillsMissingTagSelection.length > 0) {
      return "Select at least one tag for each selected skill";
    }
    if (totalWeightage !== 100) return "Total skill weightage should be exactly 100";
    return null;
  };

  const handleSubmit = async () => {
    const errorMsg = validate();
    if (errorMsg) {
      message.error(errorMsg);
      return;
    }

    setIsSubmitting(true);
    setLoadingText(editingScenarioId ? 'Updating scenario...' : 'Creating Scenario...');

    try {
      const learnerRoleIds = selectedPersonas
        .map((key) => resolvePersonaId(key, availablePersonas))
        .filter((id) => id != null);

      const tagIds = selectedTagIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id));

      const skillsFromSelectedTags = [
        ...new Set(
          tagIds
            .map((tagId) => findTagById(tagId))
            .filter(Boolean)
            .map((tag) =>
              tag.skillName != null && String(tag.skillName).trim() !== ''
                ? String(tag.skillName).trim()
                : null
            )
            .filter(Boolean)
        ),
      ];

      if (tagIds.length === 0 || skillsFromSelectedTags.length === 0) {
        message.error('Could not resolve selected skill tags. Try re-selecting skills.');
        setIsSubmitting(false);
        return;
      }

      if (learnerRoleIds.length === 0) {
        message.error('Could not resolve target learner persona IDs. Try re-selecting personas.');
        setIsSubmitting(false);
        return;
      }

      const rolePlayAttachments = existingAttachments.map((a) => ({
        id: a.id,
        title: a.title,
        contentType: a.contentType,
        s3Key: a.s3Key,
        configJson: a.configJson != null && String(a.configJson).trim() !== '' ? a.configJson : '{}',
      }));

      const skillWeightagesPayload = {};
      skillsFromSelectedTags.forEach((keyStr) => {
        const n = Number(skillWeightages[keyStr] ?? 0);
        skillWeightagesPayload[keyStr] = Number.isNaN(n) ? 0 : n;
      });

      const payload = {
        vendorId: selectedVendorId ?? null,
        privateScenario: rolePlayType !== 'Public',
        archived,
        published,
        recommended,
        difficulty: enumOrNull(difficulty),
        rolePlayAgentId: selectedCoach ?? null,
        mode,
        title: title.trim(),
        description: description.trim(),
        durationInMinutes: Number.parseInt(interactionTime, 10),
        maxAttempts: Number(maxAttempts),
        imageS3Key: imageS3Key || null,
        imagePreviewS3Key: imagePreviewS3Key || null,
        videoPreviewS3Key: videoPreviewS3Key || null,
        openingMessage: emptyStrToNull(openingMessage),
        openingPromptSuggestions: Array.isArray(conversationPrompts) ? conversationPrompts : [],
        companyContextPrompt: emptyStrToNull(companyContextPrompt),
        language: enumOrNull(language),
        companyName: emptyStrToNull(companyName),
        industry: enumOrNull(industry),
        companySize: enumOrNull(companySize),
        region: enumOrNull(region),
        relationshipType: enumOrNull(relationshipType),
        buyerMood: enumOrNull(buyerMood),
        buyerPressure: enumOrNull(buyerPressure),
        buyerStatePrompt: emptyStrToNull(buyerStatePrompt),
        communicationTone: enumOrNull(communicationTone),
        communicationTonePrompt: emptyStrToNull(communicationTonePrompt),
        replyBehaviour: enumOrNull(replyBehaviour),
        replyBehaviourPrompt: emptyStrToNull(replyBehaviourPrompt),
        callArcPrompt: callArcPrompt.trim(),
        customAgentRules: emptyStrToNull(customAgentRules),
        objections: Array.isArray(objections) ? objections : [],
        skillWeightages: skillWeightagesPayload,
        tagIds,
        learnerRoleIds,
        rolePlayAttachments: Array.isArray(rolePlayAttachments) ? rolePlayAttachments : [],
      };

      payload.interruptLogicPrompt = mode === 'COACH' ? emptyStrToNull(interruptLogicPrompt) : null;
      payload.coachFlowPrompt = mode === 'COACH' ? emptyStrToNull(coachFlowPrompt) : null;

      const response = editingScenarioId
        ? await api.updateRoleplayScenario(editingScenarioId, payload)
        : await api.createRoleplayScenario(payload);

      if (response.data.success) {
        const scenarioId =
          response.data.data?.id ?? response.data.data?.data?.id ?? editingScenarioId;
        // Verify we have an ID
        if (!scenarioId) {
          message.warning("Scenario created, but ID missing for uploads.");
          setIsSubmitting(false);
          return;
        }

        // Handle File Uploads
        if (files.length > 0) {
          for (let i = 0; i < files.length; i++) {
            const file = files[i];
            setLoadingText(`Uploading file ${i + 1} of ${files.length}...`);

            const urlRes = await api.getRoleplayScenarioAttachmentUploadUrl(scenarioId, {
              fileName: file.name,
              contentType: file.type,
            });

            if (urlRes.data.success && urlRes.data.data) {
              const { url, s3Key, signedHeaders } = urlRes.data.data;

              await axios.put(url, file, {
                headers: {
                  ...signedHeaders,
                  'Content-Type': file.type
                }
              });

              await api.createRoleplayScenarioAttachment(scenarioId, {
                s3Key,
                title: file.name,
                contentType: file.type
              });
            }
          }
        }

        message.success(
          editingScenarioId
            ? 'Role play scenario updated successfully!'
            : 'Role Play Scenario created successfully!'
        );
        navigate('/roleplays');

      } else {
        message.error(
          (editingScenarioId ? 'Failed to update' : 'Failed to create') +
            ' scenario: ' +
            (response.data.message || 'Unknown error')
        );
      }

    } catch (error) {
      console.error(editingScenarioId ? 'Error updating scenario:' : 'Error creating scenario:', error);
      message.error(
        editingScenarioId
          ? 'Error updating scenario. Please try again.'
          : 'Error creating scenario. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
      setLoadingText('');
    }
  };

  if (isLoading) {
    return (
      <AppLayout disableVendorSelect>
        <div className={`min-h-[calc(100vh-120px)] ${PAGE_BG} flex items-center justify-center rounded-lg`}>
          <Spin size="large" tip="Loading configuration..." />
        </div>
      </AppLayout>
    );
  }

  let submitButtonLabel = 'Create scenario';
  if (isSubmitting) {
    submitButtonLabel = editingScenarioId ? 'Saving…' : 'Creating…';
  } else if (editingScenarioId) {
    submitButtonLabel = 'Save changes';
  }

  return (
    <AppLayout disableVendorSelect>
      <div className={`w-full ${PAGE_BG} min-h-[calc(100vh-64px)] py-6 sm:py-8`}>
        <div className="max-w-[min(100%,96rem)] w-full mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-6 lg:mb-10 max-w-4xl">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
                {editingScenarioId ? 'Edit role-play scenario' : 'New role-play scenario'}
              </h1>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs font-medium text-slate-600 mt-1.5">
                {vendorLabel}
              </span>
            </div>
            <p className="mt-2 text-sm sm:text-[15px] text-slate-600 leading-relaxed">
              {editingScenarioId
                ? 'Update prompts, configuration, and skill weightages for this scenario.'
                : 'Configure your scenario settings, prompts, and skill weightages before publishing.'}
            </p>
          </header>

          <div className="flex flex-col xl:flex-row gap-8 xl:items-start min-w-0">
            <div className="w-full xl:basis-[60%] xl:max-w-[60%] min-w-0 xl:max-h-[calc(100vh-5.5rem)] xl:overflow-y-auto xl:pr-2">
          <div className="mb-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Build</div>
          <Spin spinning={isSubmitting} tip={loadingText}>

            {/* AI GENERATE FROM PROMPT */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <button
                type="button"
                onClick={() => setAiExpanded(!aiExpanded)}
                className="w-full flex items-center justify-between text-left"
              >
                <h2 className="text-lg font-semibold text-slate-900">
                  ✦ Generate from Prompt
                </h2>
                <span className="text-slate-400 text-sm">{aiExpanded ? '▲' : '▼'}</span>
              </button>
              {aiExpanded && (
                <div className="mt-4">
                  <div className="mb-3">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Describe the scenario you want to create... e.g. I want to practice discovery calls with a skeptical CTO in a SaaS startup facing budget pressure"
                      rows={3}
                      className={`${inputBaseClass} resize-y`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleAiGenerate}
                    disabled={refinementsLoading || aiLoading || !aiPrompt.trim()}
                    className={`inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-colors ${
                      refinementsLoading || aiLoading || !aiPrompt.trim()
                        ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                        : 'bg-[#0842A0] text-white hover:bg-[#063a8c]'
                    }`}
                  >
                    {refinementsLoading || aiLoading ? (
                      <>
                        <Spin size="small" />
                        {hasAiGeneratedOnce ? 'Regenerating scenario...' : 'Analyzing your prompt...'}
                      </>
                    ) : hasAiGeneratedOnce ? (
                      '🔄 Regenerate with AI'
                    ) : (
                      '🤖 Generate with AI'
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* REFINEMENT LOADING — shown while analyzing prompt */}
            {refinementsLoading && (
              <div className={`${CARD} ${CARD_PAD} mb-6 border-2 border-[#0842A0] bg-blue-50`}>
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <Spin size="large" />
                  <p className="text-sm font-medium text-slate-700">Analyzing your prompt...</p>
                  <p className="text-xs text-slate-400">Checking for behavioral and business signals</p>
                </div>
              </div>
            )}

            {/* VAGUE PROMPT SUGGESTION — shown when prompt is too vague/short */}
            {showVagueSuggestion && (
              <div className={`${CARD} ${CARD_PAD} mb-6 border-2 border-amber-300 bg-amber-50`}>
                <div className="mb-4">
                  <h2 className="text-lg font-semibold text-slate-900">
                    We need a little more context
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    To create a realistic customer conversation try one of these:
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {[
                    'Customer is evaluating multiple vendors before making a decision.',
                    'Customer wants stronger business justification before approving the purchase.',
                    'Customer is reviewing existing vendors before contract renewal.',
                  ].map((s, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleVagueSuggestionSelect(s)}
                      className="px-4 py-4 rounded-xl text-sm border-2 transition-all text-left border-amber-200 bg-white text-slate-800 hover:border-[#0842A0] hover:shadow-md"
                    >
                      <span className="mr-2">✨</span>
                      {s}
                    </button>
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setShowVagueSuggestion(false)}
                    className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* PROMPT REFINEMENT PICKER — suggests improvements before scenario generation */}
            {showRefinementPicker && refinementsData && (
              <div className={`${CARD} ${CARD_PAD} mb-6 border-2 border-[#0842A0] bg-blue-50`}>
                <div className="mb-4">
                  <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold mb-1">Prompt Assistant</p>
                  <p className="text-sm text-slate-700 mb-3">
                    Determines how much AI assistance should be provided based on evaluation of customer behavior signal, business situation signal, and prompt clarity.
                  </p>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {refinementsData.category === 'needs_enrichment' ? 'Needs Enrichment' :
                     refinementsData.category === 'needs_refinement' ? 'Needs Refinement' : 'Ready'}
                  </h2>
                  <p className="text-sm text-slate-600 mt-1">
                    {refinementsData.category === 'needs_enrichment' ? 'Prompt is vague or incomplete. Generate 5 AI refinements.' :
                     refinementsData.category === 'needs_refinement' ? 'Prompt is understandable but ambiguous. Generate 3 AI refinements.' :
                     'Prompt already contains strong behavioral and business signals. Generate 2 AI polish suggestions.'}
                  </p>
                  <p className="text-xs text-slate-500 mt-2">
                    <span className="font-medium">Purpose:</span> {refinementsData.purpose}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {refinementsData.refinements.map((r, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleRefinementSelect(r.text)}
                      className="px-4 py-4 rounded-xl text-sm border-2 transition-all text-left border-slate-200 bg-white text-slate-800 hover:border-[#0842A0] hover:shadow-md"
                    >
                      <div className="text-xs text-slate-400 font-semibold uppercase tracking-wide mb-1">
                        Suggestion {i + 1}
                      </div>
                      <div className="leading-snug mb-2">{r.text}</div>
                      <div className="text-xs text-slate-500 italic">{r.reason}</div>
                    </button>
                  ))}
                </div>
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => { setShowRefinementPicker(false); setRefinementsData(null); proceedToScenario(aiPrompt); }}
                    className="text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
                  >
                    Use my original prompt instead
                  </button>
                </div>
              </div>
            )}

            {/* SCENARIO LOADING — shown while generating scenario */}
            {aiLoading && (
              <div className={`${CARD} ${CARD_PAD} mb-6 border-2 border-[#0842A0] bg-blue-50`}>
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <Spin size="large" />
                  <p className="text-sm font-medium text-slate-700">Generating your scenario...</p>
                  <p className="text-xs text-slate-400">Building buyer profile, objections, and conversation flow</p>
                </div>
              </div>
            )}



            {/* SCENARIO MODE */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Scenario mode
              </h2>
              <div className="mb-2">
                <FormLabel>Scenario Mode</FormLabel>
                <div className="flex flex-wrap gap-3">
                  <RolePlayOption label="Sales Mode" selected={mode === 'SALES'} onClick={() => setMode('SALES')} />
                  <RolePlayOption label="Coach Mode" selected={mode === 'COACH'} onClick={() => setMode('COACH')} />
                </div>
              </div>
            </div>

            {/* SCENARIO SETUP */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Scenario setup
              </h2>
              <div className="mb-6">
                <FormLabel required>Scenario Title</FormLabel>
                <CustomInput
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter scenario title (e.g., Renewal negotiation with at-risk account)"
                />
              </div>
              <div className="mb-6">
                <FormLabel required>Scenario Description</FormLabel>
                <CustomInput
                  type="textarea"
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter scenario description..."
                />
              </div>
              <div className="mb-8 text-left">
                <FormLabel>Cover image</FormLabel>
                <p className="text-sm text-slate-500 mb-3">
                  Optional. Upload is stored in S3; the returned key is saved with the scenario.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="flex-1 min-w-0 w-full">
                    <button
                      type="button"
                      disabled={coverImageUploading}
                      onClick={() => coverImageInputRef.current?.click()}
                      className={`w-full border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center cursor-pointer bg-slate-50/50 hover:bg-slate-50 hover:border-slate-400 transition-colors ${
                        coverImageUploading ? 'opacity-60 pointer-events-none' : ''
                      }`}
                    >
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" strokeWidth={1.25} />
                      <p className="text-sm text-slate-800 font-medium">
                        {coverImageUploading ? 'Uploading…' : 'Choose cover image'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPG, WebP, or GIF</p>
                      <input
                        type="file"
                        ref={coverImageInputRef}
                        hidden
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={handleCoverImageSelect}
                      />
                    </button>
                  </div>
                  {(imageS3Key || coverImageUrl) ? (
                    <div className="w-full sm:w-48 shrink-0 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden relative">
                      <img
                        src={coverImageUrl || getS3Url(imageS3Key)}
                        alt="Cover preview"
                        className="w-full h-32 object-cover"
                      />
                      <div className="p-2 border-t border-slate-200 bg-white flex justify-between items-center gap-2">
                        <span className="text-[10px] text-slate-500 truncate" title={imageS3Key}>
                          {imageS3Key}
                        </span>
                        <button
                          type="button"
                          onClick={clearCoverImage}
                          className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                          aria-label="Remove cover image"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="mb-8 text-left">
                <FormLabel>Preview media</FormLabel>
                <p className="text-sm text-slate-500 mb-3">
                  Optional image and video previews stored under roleplay scenario media paths.
                </p>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/40">
                    <div className="text-sm font-medium text-slate-800 mb-2">Image preview</div>
                    <button
                      type="button"
                      disabled={imagePreviewUploading}
                      onClick={() => imagePreviewInputRef.current?.click()}
                      className={`w-full border-2 border-dashed border-slate-300 rounded-xl p-5 text-center cursor-pointer bg-white hover:bg-slate-50 hover:border-slate-400 transition-colors ${
                        imagePreviewUploading ? 'opacity-60 pointer-events-none' : ''
                      }`}
                    >
                      <Upload className="w-7 h-7 text-slate-400 mx-auto mb-2" strokeWidth={1.25} />
                      <p className="text-sm text-slate-800 font-medium">
                        {imagePreviewUploading ? 'Uploading…' : 'Choose image preview'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">PNG, JPG, WebP, or GIF</p>
                      <input
                        type="file"
                        ref={imagePreviewInputRef}
                        hidden
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={handleImagePreviewSelect}
                      />
                    </button>
                    {(imagePreviewS3Key || imagePreviewUrl) ? (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <img
                          src={imagePreviewUrl || getS3Url(imagePreviewS3Key)}
                          alt="Image preview"
                          className="w-full h-28 object-cover"
                        />
                        <div className="p-2 border-t border-slate-200 flex justify-between items-center gap-2">
                          <span className="text-[10px] text-slate-500 truncate" title={imagePreviewS3Key}>
                            {imagePreviewS3Key}
                          </span>
                          <button
                            type="button"
                            onClick={clearImagePreview}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                            aria-label="Remove image preview"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="rounded-xl border border-slate-200 p-4 bg-slate-50/40">
                    <div className="text-sm font-medium text-slate-800 mb-2">Video preview</div>
                    <button
                      type="button"
                      disabled={videoPreviewUploading}
                      onClick={() => videoPreviewInputRef.current?.click()}
                      className={`w-full border-2 border-dashed border-slate-300 rounded-xl p-5 text-center cursor-pointer bg-white hover:bg-slate-50 hover:border-slate-400 transition-colors ${
                        videoPreviewUploading ? 'opacity-60 pointer-events-none' : ''
                      }`}
                    >
                      <Upload className="w-7 h-7 text-slate-400 mx-auto mb-2" strokeWidth={1.25} />
                      <p className="text-sm text-slate-800 font-medium">
                        {videoPreviewUploading ? 'Uploading…' : 'Choose video preview'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">MP4, MOV, or WebM</p>
                      <input
                        type="file"
                        ref={videoPreviewInputRef}
                        hidden
                        accept=".mp4,.mov,.webm,video/mp4,video/quicktime,video/webm"
                        onChange={handleVideoPreviewSelect}
                      />
                    </button>
                    {(videoPreviewS3Key || videoPreviewUrl) ? (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-white overflow-hidden">
                        <video
                          src={videoPreviewUrl || getS3Url(videoPreviewS3Key)}
                          controls
                          className="w-full h-28 object-cover bg-black"
                        />
                        <div className="p-2 border-t border-slate-200 flex justify-between items-center gap-2">
                          <span className="text-[10px] text-slate-500 truncate" title={videoPreviewS3Key}>
                            {videoPreviewS3Key}
                          </span>
                          <button
                            type="button"
                            onClick={clearVideoPreview}
                            className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                            aria-label="Remove video preview"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="mb-6">
                <FormLabel required>Target Learner Personas</FormLabel>
                {personaMatchFailed && selectedPersonas.length === 0 && (
                  <div className="p-3 rounded-xl mb-3 bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-center gap-2">
                    <span>⚠️</span>
                    <span>No persona matched — please select one</span>
                  </div>
                )}
                <div className="p-4 rounded-xl mb-4 min-h-[52px] flex flex-wrap gap-2 bg-slate-50 border border-slate-200">
                  {selectedPersonas.map((p) => {
                    const persona = availablePersonas.find((x) => getPersonaDisplayKey(x) === p);
                    return (
                      <ActivePill
                        key={p}
                        text={p}
                        imageUrl={persona?.imageUrl}
                        onRemove={() => handleToggle(p, selectedPersonas, setSelectedPersonas)}
                      />
                    );
                  })}
                </div>
                <div className="flex flex-wrap gap-2">
                  {availablePersonas
                    .filter((p) => !selectedPersonas.includes(getPersonaDisplayKey(p)))
                    .map((p) => {
                      const personaKey = getPersonaDisplayKey(p);
                      return (
                    <SuggestionPill
                      key={p.id}
                      text={personaKey}
                      imageUrl={p.imageUrl}
                      onAdd={() => handleToggle(personaKey, selectedPersonas, setSelectedPersonas)}
                    />
                      );
                    })}
                </div>
              </div>
            </div>

            {/* COMPANY CONTEXT */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Buyer Profile
              </h2>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    SELECT BUYER PERSONA <span className="text-red-500 ml-1">*</span>
                  </label>
                  <a
                    href="/roleplays/buyer-profiles"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0842A0] hover:underline shrink-0"
                  >
                    Configure buyer profiles →
                  </a>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {coaches.map((coach) => (
                    <div
                      key={coach.id}
                      onClick={() => applyCoachToScenario(coach)}
                      className={`group relative flex items-center justify-between p-3.5 rounded-xl border text-left transition-all cursor-pointer ${
                        Number(selectedCoach) === Number(coach.id)
                          ? 'border-[#0842A0] bg-[#f0f6ff] ring-1 ring-[#0842A0]/20 shadow-sm'
                          : 'border-slate-200 bg-slate-50/80 hover:border-slate-300 hover:bg-white'
                      }`}
                    >
                      <div className="flex items-center min-w-0 mr-2">
                        <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center mr-3 text-lg shrink-0 overflow-hidden">
                          <img
                            src={coach.avatarImageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.textContent = '👤'; }}
                          />
                        </div>
                        <div className="text-left min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{coach.name}</div>
                          <div className="text-xs text-slate-500 truncate">{coach.subtitle}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* DIFFICULTY */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Difficulty
              </h2>
              <div className="flex flex-wrap gap-3">
                {ENUM_OPTIONS.difficulty.map((option) => (
                  <RolePlayOption
                    key={option.value}
                    label={option.label}
                    selected={difficulty === option.value}
                    onClick={() => handleDifficultyChange(option.value)}
                  />
                ))}
              </div>
            </div>

            {/* BUYER BEHAVIOUR */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Buyer Behaviour
              </h2>
              <div className="mb-6">
                <FormLabel>Buyer Mood</FormLabel>
                <CustomInput
                  type="select"
                  value={buyerMood}
                  onChange={(e) => {
                    const mood = e.target.value;
                    setBuyerMood(mood);
                    const mapping = MOOD_PRESSURE_TO_BEHAVIOR[mood]?.[buyerPressure] || MOOD_DEFAULT_BEHAVIOR[mood];
                    if (mapping) {
                      setCommunicationTone(mapping.tone);
                      setReplyBehaviour(mapping.reply);
                    } else if (mood) {
                      setCommunicationTone('');
                      setReplyBehaviour('');
                    }
                    setBuyerStatePrompt('');
                    handleRegenerateBehaviorPrompts({ buyerMood: mood || null, buyerPressure: buyerPressure || null, regenerateOnly: 'buyerStatePrompt' });
                  }}
                >
                  <option value="">Select…</option>
                  {getMoodOptions(difficulty).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </CustomInput>
              </div>
              <div className="mb-6">
                <FormLabel>Buyer Pressure</FormLabel>
                <CustomInput
                  type="select"
                  value={buyerPressure}
                  onChange={(e) => {
                    const pressure = e.target.value;
                    setBuyerPressure(pressure);
                    const mapping = MOOD_PRESSURE_TO_BEHAVIOR[buyerMood]?.[pressure] || MOOD_DEFAULT_BEHAVIOR[buyerMood];
                    if (mapping) {
                      setCommunicationTone(mapping.tone);
                      setReplyBehaviour(mapping.reply);
                    } else if (pressure) {
                      setCommunicationTone('');
                      setReplyBehaviour('');
                    }
                    setBuyerStatePrompt('');
                    handleRegenerateBehaviorPrompts({ buyerMood: buyerMood || null, buyerPressure: pressure || null, regenerateOnly: 'buyerStatePrompt' });
                  }}
                >
                  <option value="">Select…</option>
                  {getPressureOptions(difficulty).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </CustomInput>
              </div>

            </div>

            {/* AI BEHAVIOR PROMPTS */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                AI behavior prompts
              </h2>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <FormLabel required>Call Flow Prompt</FormLabel>
                  {isCallFlowEditable ? (
                    <button type="button" onClick={() => setIsCallFlowEditable(false)}
                      className="flex items-center gap-1 text-xs font-medium text-[#0842A0] hover:text-blue-700">
                      <Save className="w-3.5 h-3.5" /> Save
                    </button>
                  ) : (
                    <button type="button" onClick={() => setIsCallFlowEditable(true)}
                      className="flex items-center gap-1 text-xs font-medium text-slate-400 hover:text-slate-600">
                      <Pen className="w-3.5 h-3.5" /> Edit
                    </button>
                  )}
                </div>
                {!isCallFlowEditable && (
                  <p className="text-xs text-slate-500 mb-2">
                    This prompt is prefilled and locked.
                  </p>
                )}
                <CustomInput
                  type="textarea"
                  rows={4}
                  value={callArcPrompt}
                  onChange={(e) => isCallFlowEditable && setCallArcPrompt(e.target.value)}
                  disabled={!isCallFlowEditable}
                  placeholder="Open -> discovery -> objections -> close..."
                />
              </div>

              {mode === 'COACH' && (
                <>
                  <div className="mb-6">
                    <FormLabel>Interrupt Logic Prompt</FormLabel>
                    <CustomInput
                      type="textarea"
                      rows={3}
                      value={interruptLogicPrompt}
                      onChange={(e) => setInterruptLogicPrompt(e.target.value)}
                      placeholder="Define when the scenario should interrupt and switch behavior..."
                    />
                  </div>
                  <div className="mb-2">
                    <FormLabel>Coach Flow Prompt</FormLabel>
                    <CustomInput
                      type="textarea"
                      rows={3}
                      value={coachFlowPrompt}
                      onChange={(e) => setCoachFlowPrompt(e.target.value)}
                      placeholder="Define the coach guidance flow after interruption..."
                    />
                  </div>
                </>
              )}
            </div>

            {/* ADD OBJECTION */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-1 pb-4 border-b border-slate-100">
                Add Objection
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Objections are auto-generated when creating a scenario with AI. You can also add custom ones manually.
              </p>

              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <CustomInput
                  value={objectionInput}
                  onChange={(e) => setObjectionInput(e.target.value)}
                  placeholder="e.g., Your pricing is too high for our budget"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddObjection()}
                />
                <button
                  type="button"
                  onClick={handleAddObjection}
                  className="shrink-0 px-5 py-2.5 bg-[#0842A0] text-white rounded-xl text-sm font-semibold shadow-sm hover:bg-[#063a8c] transition-colors"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={handleRegenerateObjections}
                  disabled={objectionRegenLoading || (!aiPrompt.trim() && !title.trim() && !description.trim())}
                  className="shrink-0 px-5 py-2.5 bg-white text-slate-700 rounded-xl text-sm font-semibold shadow-sm border border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {objectionRegenLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Generating...
                    </span>
                  ) : (
                    'Regenerate'
                  )}
                </button>
              </div>

              {objections.length > 0 && (
                <div className="space-y-2 mt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Objections ({objections.length})
                  </div>
                  {objections.map((obj, index) => (
                    <div
                      key={index}
                      className="flex gap-2 items-center bg-slate-50 p-3 rounded-xl border border-slate-200"
                    >
                      <span className="flex-1 text-sm text-slate-700">{obj}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveObjection(index)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        aria-label="Remove objection"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* INDUSTRY */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-1 pb-4 border-b border-slate-100">
                Industry <span className="text-red-500">*</span>
              </h2>
              <p className="text-sm text-slate-500 mb-4">
                Select an industry to load the relevant skills and tags. Choosing a buyer persona will apply that profile&apos;s industry automatically.
              </p>
              <CustomInput
                type="select"
                value={industry}
                onChange={handleIndustryChange}
              >
                <option value="">Select industry…</option>
                {ENUM_OPTIONS.industry.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </CustomInput>
            </div>

            {/* SKILLS */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Skills <span className="text-red-500">*</span>
              </h2>
              {!industry ? (
                <p className="text-sm text-slate-500">Select an industry above to view available skills.</p>
              ) : tagsLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-slate-600">
                  <Spin size="small" />
                  Loading skills for {labelForEnum(ENUM_OPTIONS.industry, industry)}…
                </div>
              ) : (
              <>
              <div className="space-y-3">
                {skillNamesFromTags.length === 0 && (
                  <p className="text-sm text-slate-500">No skills found for this industry.</p>
                )}
                {skillNamesFromTags.map((skillName) => {
                  const isChecked = selectedSkillNames.includes(skillName);
                  const skillTagDefs = tagsBySkillName.get(skillName) || [];
                  return (
                    <div key={skillName} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between gap-3">
                        <label className="flex items-center gap-2 text-sm font-semibold text-slate-900 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSkillSelection(skillName)}
                            className="rounded border-slate-300"
                          />
                          <span>{skillName}</span>
                        </label>
                        {isChecked ? (
                          <div className="flex flex-col items-end">
                            <input
                              id={`weight-${skillName}`}
                              type="number"
                              min={0}
                              max={100}
                              value={skillWeightages[skillName] ?? ''}
                              onChange={(e) =>
                                setSkillWeightages((prev) => ({ ...prev, [skillName]: e.target.value }))
                              }
                              className={`w-24 px-2.5 py-1.5 rounded-md border text-sm ${
                                skillWeightErrors[skillName]
                                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                                  : 'border-slate-300'
                              }`}
                              placeholder="0"
                              aria-label={`Weight for ${skillName}`}
                            />
                            {skillWeightErrors[skillName] ? (
                              <span className="mt-1 text-[11px] text-red-600">{skillWeightErrors[skillName]}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      {isChecked && (
                        <>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {skillTagDefs.map(({ id: tagId, label: tagLabel }) => {
                              const isTagSelected = selectedTagIds.includes(Number(tagId));
                              return (
                                <button
                                  type="button"
                                  key={`${skillName}-tag-${tagId}`}
                                  onClick={() => handleTagToggle(tagId, skillName)}
                                  className={`px-2 py-1 rounded-md border text-xs ${
                                    isTagSelected
                                      ? 'bg-slate-200 text-slate-800 border-slate-300'
                                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                                  }`}
                                >
                                  {isTagSelected ? '' : '+'} {tagLabel}
                                </button>
                              );
                            })}
                          </div>
                          <div className="mt-3">
                            <div className="text-[11px] font-medium text-slate-500 mb-1">Selected tags</div>
                            <div className="flex flex-wrap gap-2">
                              {selectedTagIds
                                .filter((id) => {
                                  const tag = findTagById(id);
                                  const sk =
                                    tag?.skillName != null && String(tag.skillName).trim() !== ''
                                      ? String(tag.skillName).trim()
                                      : null;
                                  return sk === skillName;
                                })
                                .map((id) => {
                                  const tag = findTagById(id);
                                  const tagLabel = tag?.label || tag?.name || String(id);
                                  return (
                                    <span
                                      key={`${skillName}-selected-${id}`}
                                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-200 text-slate-800 text-xs"
                                    >
                                      {tagLabel}
                                      <button
                                        type="button"
                                        onClick={() => handleTagToggle(id, skillName)}
                                        className="text-slate-600 hover:text-slate-900"
                                        aria-label={`Remove ${tagLabel}`}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-700">
                  Total Weight: {totalWeightage} / 100
                </p>
                {selectedSkillNames.length > 0 && totalWeightage !== 100 && (
                  <p className="text-sm text-red-600 mt-1">
                    Total skill weightage should be 100.
                  </p>
                )}
                {skillsMissingTagSelection.length > 0 && (
                  <p className="text-sm text-red-600 mt-1">
                    Select at least one tag for each selected skill.
                  </p>
                )}
              </div>
              </>
              )}
            </div>

            {/* SESSION SETTINGS */}
            <div className={`${CARD} ${CARD_PAD} mb-6`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Session settings
              </h2>
              <div className="mb-8">
                <FormLabel required>Role-Play Type</FormLabel>
                <div className="flex flex-wrap gap-3">
                  <RolePlayOption label="Public" selected={rolePlayType === 'Public'} onClick={() => setRolePlayType('Public')} />
                  <RolePlayOption label="Partner only" selected={rolePlayType === 'Partner only'} onClick={() => setRolePlayType('Partner only')} />
                </div>
              </div>

              <div className="mb-8">
                <FormLabel required>Recommended</FormLabel>
                <div className="flex flex-wrap gap-3">
                  <RolePlayOption
                    label="Yes"
                    selected={recommended === true}
                    onClick={() => setRecommended(true)}
                  />
                  <RolePlayOption
                    label="No"
                    selected={recommended === false}
                    onClick={() => setRecommended(false)}
                  />
                </div>
              </div>
              <div className="mb-6">
                <FormLabel required>Expected Interaction Time</FormLabel>
                <InteractionToggle options={['3', '5', '10', '15', '20']} value={interactionTime} onChange={setInteractionTime} />
              </div>
              <div className="mb-2">
                <FormLabel required>Max Attempts</FormLabel>
                <CustomInput
                  type="number"
                  min={1}
                  value={maxAttempts}
                  onChange={(e) => setMaxAttempts(e.target.value)}
                  placeholder="3"
                />
              </div>
            </div>

            {/* BUYER + SELLER PROMPTS + KNOWLEDGE */}
            <div className={`${CARD} ${CARD_PAD} mb-6 text-left`}>
              <h2 className="text-lg font-semibold text-slate-900 mb-1 pb-4 border-b border-slate-100">
                Suggested opening prompts & knowledge upload
              </h2>
              <div className="mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Buyer</div>
                <p className="text-xs text-slate-500">The buyer's first line that kicks off the role-play session</p>
              </div>
              <FormLabel>Scenario kick-off</FormLabel>
              <CustomInput
                type="textarea"
                rows={3}
                value={openingMessage}
                onChange={(e) => setOpeningMessage(e.target.value)}
                placeholder="Hi, thanks for reaching out. I'll be honest — we've been reviewing our expenses..."
              />

              <div className="mt-6 mb-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Seller</div>
                <p className="text-xs text-slate-500">Starter prompts shown to learners to help them begin the conversation</p>
              </div>
              <FormLabel>Add starter prompts</FormLabel>

              <div className="flex flex-col sm:flex-row gap-2 mb-4">
                <CustomInput
                  value={promptInput}
                  onChange={(e) => setPromptInput(e.target.value)}
                  placeholder="e.g., How do I handle pricing objections?"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddPrompt()}
                />
                <button
                  type="button"
                  onClick={handleAddPrompt}
                  className="shrink-0 px-5 py-2.5 bg-[#0842A0] text-white rounded-xl text-sm font-semibold shadow-sm hover:bg-[#063a8c] transition-colors"
                >
                  Add
                </button>
                <button
                  type="button"
                  onClick={handleRegenerateConversationPrompts}
                  disabled={convPromptRegenLoading || (!aiPrompt.trim() && !title.trim() && !description.trim())}
                  className="shrink-0 px-5 py-2.5 bg-white text-slate-700 rounded-xl text-sm font-semibold shadow-sm border border-slate-300 hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {convPromptRegenLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Generating...
                    </span>
                  ) : (
                    'Regenerate'
                  )}
                </button>
              </div>

              <div className="space-y-2 mb-4">
                {conversationPrompts.map((prompt, index) => (
                  <div
                    key={index}
                    className="flex gap-2 items-center bg-slate-50 p-3 rounded-xl border border-slate-200"
                  >
                    <span className="flex-1 text-sm text-slate-700">{prompt}</span>
                    <button
                      type="button"
                      onClick={() => handleRemovePrompt(index)}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      aria-label="Remove prompt"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mb-6">These prompts help learners begin confidently.</p>

              <div className="text-sm font-semibold text-slate-900 mb-1">Knowledge upload</div>
              <p className="text-sm text-slate-500 mb-6">Optional reference files for the agent (max 3).</p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 rounded-2xl p-8 sm:p-10 text-center cursor-pointer bg-slate-50/50 hover:bg-slate-50 hover:border-slate-400 transition-colors mb-2"
              >
                <Upload className="w-9 h-9 text-slate-400 mx-auto mb-3" strokeWidth={1.25} />
                <p className="text-base text-slate-800 font-medium">
                  Drop files or <span className="text-[#0842A0] font-semibold">browse</span>
                </p>
                <p className="text-sm text-slate-500 mt-1">PDF, MP4, MOV, PNG, JPG, WebP, or GIF — up to 3 files</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  hidden
                  multiple
                  accept=".pdf,.mp4,.mov,.png,.jpg,.jpeg,.webp,.gif,image/png,image/jpeg,image/webp,image/gif,application/pdf,video/mp4,video/quicktime"
                  onChange={handleFileSelect}
                />
              </button>

              {existingAttachments.length > 0 && (
                <div className="space-y-4 mt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Saved attachments
                  </div>
                  {existingAttachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-slate-200 bg-white shadow-sm"
                    >
                      <div className="min-w-0 flex-1 flex flex-col sm:flex-row sm:items-center sm:gap-2">
                        <span className="text-sm font-medium text-slate-800 truncate" title={att.title}>
                          {att.title}
                        </span>
                        <span className="text-xs text-slate-500 shrink-0">Not editable</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {files.length > 0 && (
                <div className="space-y-2 mt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    New files (upload on save)
                  </div>
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between gap-3 p-3 rounded-xl bg-white border border-slate-200 shadow-sm"
                    >
                      <span className="text-sm font-medium text-slate-700 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                        aria-label="Remove file"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* SUBMIT BUTTON */}
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 mb-8">
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`inline-flex items-center justify-center px-8 py-3.5 bg-[#0842A0] text-white rounded-xl font-semibold text-base shadow-sm hover:bg-[#063a8c] transition-colors ${
                  isSubmitting ? 'opacity-60 cursor-not-allowed pointer-events-none' : ''
                }`}
              >
                {submitButtonLabel}
              </button>
            </div>

          </Spin>
            </div>
            <aside className="w-full xl:basis-[40%] xl:max-w-[40%] shrink-0 xl:sticky xl:top-20 xl:self-start xl:max-h-[calc(100vh-5.5rem)] xl:overflow-y-auto">
              <div className="mb-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Preview</div>
              <ScenarioLivePreview
                title={title}
                description={description}
                coverImageUrl={coverImageUrl}
                imageS3Key={imageS3Key}
                imagePreviewUrl={imagePreviewUrl}
                imagePreviewS3Key={imagePreviewS3Key}
                videoPreviewUrl={videoPreviewUrl}
                videoPreviewS3Key={videoPreviewS3Key}
                mode={mode}
                language={language}
                industry={industry}
                companySize={companySize}
                region={region}
                relationshipType={relationshipType}
                difficulty={difficulty}
                buyerMood={buyerMood}
                onBuyerMoodChange={(v) => {
                  setBuyerMood(v);
                  setBuyerStatePrompt('');
                  handleRegenerateBehaviorPrompts({ buyerMood: v || null, regenerateOnly: 'buyerStatePrompt' });
                }}
                buyerPressure={buyerPressure}
                onBuyerPressureChange={(v) => {
                  setBuyerPressure(v);
                  setBuyerStatePrompt('');
                  handleRegenerateBehaviorPrompts({ buyerPressure: v || null, regenerateOnly: 'buyerStatePrompt' });
                }}
                communicationTone={communicationTone}
                onCommunicationToneChange={(v) => {
                  setCommunicationTone(v);
                  setCommunicationTonePrompt('');
                  handleRegenerateBehaviorPrompts({ communicationTone: v || null, regenerateOnly: 'communicationTonePrompt' });
                }}
                replyBehaviour={replyBehaviour}
                onReplyBehaviourChange={(v) => {
                  setReplyBehaviour(v);
                  setReplyBehaviourPrompt('');
                  handleRegenerateBehaviorPrompts({ replyBehaviour: v || null, regenerateOnly: 'replyBehaviourPrompt' });
                }}
                buyerStatePrompt={buyerStatePrompt}
                onBuyerStatePromptChange={setBuyerStatePrompt}
                communicationTonePrompt={communicationTonePrompt}
                onCommunicationTonePromptChange={setCommunicationTonePrompt}
                replyBehaviourPrompt={replyBehaviourPrompt}
                onReplyBehaviourPromptChange={setReplyBehaviourPrompt}
                companyContextPrompt={companyContextPrompt}
                callArcPrompt={callArcPrompt}
                customAgentRules={customAgentRules}
                onCustomAgentRulesChange={setCustomAgentRules}
                openingMessage={openingMessage}
                conversationPrompts={conversationPrompts}
                selectedTagPreviewItems={selectedTagPreviewItems}
                skillNamesFromTags={selectedSkillNames}
                skillWeightages={skillWeightages}
                totalWeightage={totalWeightage}
                rolePlayType={rolePlayType}
                recommended={recommended}
                interactionTime={interactionTime}
                maxAttempts={maxAttempts}
                interruptLogicPrompt={interruptLogicPrompt}
                coachFlowPrompt={coachFlowPrompt}
                selectedCoachName={selectedCoachName}
                objections={objections}
              />
            </aside>
          </div>

        </div>
      </div>
    </AppLayout>
  );
};

export default CoachingScenarioSetup;